/**
 * OpenClaw channel plugin entry point.
 *
 * Intentionally thin — no heavy imports (libsodium etc.) at module load time.
 * SecureChannel is dynamically imported inside gateway.startAccount (already async)
 * so libsodium's top-level await never runs during plugin registration.
 *
 * Loaded by OpenClaw via the `openclaw.extensions` field in package.json.
 */

import { resolve as pathResolve } from "node:path";
import { randomBytes } from "node:crypto";
import { homedir as osHomedir } from "node:os";
import {
  owesRoomReply as _owesRoomReply,
  parseMentions as _parseMentions,
  stripMentions as _stripMentions,
  stripCaptionMentions as _stripCaptionMentions,
} from "./room-protocol.js";
import { listAccountIds, resolveAccount } from "./account-config.js";
import { installFetchInterceptor, runWithTraceContext } from "./fetch-interceptor.js";
import { handleSendRequest, handleActionRequest, handleDecisionRequest, handleStatusRequest, handleTargetsRequest } from "./http-handlers.js";
import { requestHeartbeatNow } from "./openclaw-compat.js";
import { attachLifecycle, formatChannelError } from "./lifecycle.js";
import { reportPluginToolAudit } from "./tool-audit.js";
import { checkHarnessCompat } from "./harness-compat.js";
import { parseTarget } from "./types.js";
import type { DeliveryTarget } from "./types.js";
import type { SkillDefinition } from "./mcp-server.js";
import type {
  OpenClawPluginApi,
  PluginRuntime,
  ChannelGatewayContext,
  ResolvedAccountBase,
  ChannelOutboundPayloadContext,
  ReplyPayload,
  MessageSentEvent,
  SessionStartEvent,
  SessionEndEvent,
} from "./openclaw-types.js";

// --- Runtime and active channels (set during register) ---
let _ocRuntime: PluginRuntime | null = null;
const _channels = new Map<string, any>();
const _messageQueue: Array<() => Promise<void>> = [];

// --- Conversation loop prevention (A2A + rooms) ---
// Tracks recent reply timestamps per channel/room to prevent infinite loops.
// Max replies per channel within the window, then cooldown.
export const LOOP_MAX_REPLIES_PER_WINDOW = 4;
export const LOOP_WINDOW_MS = 60_000; // 1-minute sliding window
// 75s, down from 120s (#1107). Two minutes is longer than a conversation
// tolerates: a room that trips the limiter mid-exchange went quiet long enough
// to read as the agent having failed. The window and the limit are unchanged —
// only the recovery time. Safe to shorten because the @ protocol removed the
// thing the long cooldown was really absorbing (an un-mentioned message fanning
// out to every agent), and because a reply to a PERSON now bypasses the limiter
// entirely (see _roomCanReply call sites).
export const LOOP_COOLDOWN_MS = 75_000;
const _replyTimestamps = new Map<string, number[]>();
const _cooldownUntil = new Map<string, number>();

function _canReply(key: string): boolean {
  const now = Date.now();

  // Check cooldown
  const cooldownEnd = _cooldownUntil.get(key) ?? 0;
  if (now < cooldownEnd) {
    return false;
  }

  // Get timestamps within window
  const timestamps = _replyTimestamps.get(key) ?? [];
  const recent = timestamps.filter((t) => now - t < LOOP_WINDOW_MS);

  if (recent.length >= LOOP_MAX_REPLIES_PER_WINDOW) {
    // Hit limit — enter cooldown
    _cooldownUntil.set(key, now + LOOP_COOLDOWN_MS);
    _replyTimestamps.set(key, []);
    console.warn(
      `[AgentVault] Rate limit hit for ${key.slice(0, 20)}... — ` +
      `${LOOP_MAX_REPLIES_PER_WINDOW} replies in ${LOOP_WINDOW_MS / 1000}s, cooling down ${LOOP_COOLDOWN_MS / 1000}s`,
    );
    return false;
  }

  return true;
}

function _recordReply(key: string): void {
  const now = Date.now();
  const timestamps = _replyTimestamps.get(key) ?? [];
  timestamps.push(now);
  // Keep only timestamps within window
  _replyTimestamps.set(
    key,
    timestamps.filter((t) => now - t < LOOP_WINDOW_MS),
  );
}

// Back-compat aliases used internally
function _a2aCanReply(channelId: string): boolean { return _canReply(`a2a:${channelId}`); }
function _a2aRecordReply(channelId: string): void { _recordReply(`a2a:${channelId}`); }
function _roomCanReply(roomId: string): boolean { return _canReply(`room:${roomId}`); }

/**
 * May this agent send a room reply right now? (#1099 ported from hermes, #1107)
 *
 * A reply to a PERSON is never the runaway the loop guard exists for. Two
 * agents amplifying each other is; a person asking a question and getting an
 * answer is not, however many they ask.
 *
 * Live 2026-08-28: smartcode came back from a two-day MLS outage, answered the
 * replayed frames plus live traffic, tripped the 4-per-60s window — and then
 * DROPPED all three answers to the owner asking "@smartcode you still here?".
 * The recovery burst spent the budget and the person paid for it. hermes fixed
 * that in #1099 (`secure_channel.send`); the plugin never got it, so wren and
 * cortina kept dropping Chris's questions.
 *
 * An UNKNOWN sender counts as human. During the window before the room
 * directory reconciles, dropping a person's answer is worse than allowing one
 * extra agent reply — the same fail-open direction `@all` takes.
 *
 * One function, two call sites. The guard used to be written inline at both,
 * which is how a rule at N call sites becomes a rule at N-1 (#1012/#1014/#1016).
 */
export function _roomReplyAllowed(senderIsAgent: boolean | undefined, roomId: string): boolean {
  if (senderIsAgent !== true) return true;
  return _roomCanReply(roomId);
}
export function _roomRecordReply(roomId: string): void { _recordReply(`room:${roomId}`); }

/** Whether OpenClaw managed HTTP routes are active (vs self-managed server). */
/**
 * #1042 — true once the six `registerHttpRoute` calls were MADE. It cannot mean
 * "registered": OpenClaw rejects a route by pushing a diagnostic and returning
 * (never throwing, never returning a value), so the outcome is unobservable from
 * here. `openclaw plugins doctor` is the source of truth. The old name
 * (`isUsingManagedRoutes`) claimed the outcome and was false on every gateway
 * ≥ 2026.7.1 until the routes carried `auth`.
 */
export let managedRoutesRequested = false;

/**
 * Shared mutable targets array for OpenClaw outbound routing.
 * OpenClaw validates `to` against this list before calling sendText/sendPayload.
 * We mutate it at runtime to add A2A channel targets dynamically.
 */
const _outboundTargets: Array<{ id: string; label: string; accountId: string }> = [
  { id: "owner", label: "AgentVault Owner", accountId: "default" },
  { id: "default", label: "AgentVault Owner (default)", accountId: "default" },
];

// _normalizeTarget removed — replaced by parseTarget() from types.ts

/** Register a room as a valid outbound target (idempotent). */
function _registerRoomTarget(roomId: string, roomName: string, accountId: string): void {
  const targetId = `room:${roomId}`;
  if (_outboundTargets.some((t) => t.id === targetId)) return;
  _outboundTargets.push({ id: targetId, label: `Room: ${roomName}`, accountId });
  console.log(`[AgentVault] Registered room target: ${roomName} (${roomId.slice(0, 8)}...)`);
}

/** Register an A2A peer as a valid outbound target (idempotent).
 *
 * Registers multiple alias IDs that all resolve to the same A2A channel, so
 * LLMs that pick any reasonable shape (`smartcode`, `smartcode.agentvault.hub`,
 * `a2a:smartcode`, `a2a:smartcode.agentvault.hub`) pass OC's outbound target
 * validation. The deliver path normalizes back to the canonical
 * `a2a:<hubAddress>` via `_normalizeOutboundTo` before parseTarget. */
function _registerA2ATarget(hubAddress: string, accountId: string): void {
  const shortName = hubAddress.replace(/\.agentvault\.hub$/, "");
  const aliases = [
    `a2a:${hubAddress}`,           // canonical, e.g. "a2a:smartcode.agentvault.hub"
    `a2a:${shortName}`,            // "a2a:smartcode"
    hubAddress,                    // "smartcode.agentvault.hub"
    shortName,                     // "smartcode"
  ];
  let added = false;
  for (const id of aliases) {
    if (_outboundTargets.some((t) => t.id === id)) continue;
    _outboundTargets.push({ id, label: `A2A: ${hubAddress}`, accountId });
    added = true;
  }
  if (added) console.log(`[AgentVault] Registered A2A target: a2a:${hubAddress} (+aliases: ${shortName}, ${hubAddress}, a2a:${shortName})`);
}

/** Normalize a loose outbound `to` value to the canonical form parseTarget expects.
 *
 * Accepts: bare shortname ("smartcode"), bare hub ("smartcode.agentvault.hub"),
 * "a2a:<short>", "a2a:<hub>", "agent:<X>", "agentvault:..." prefixes, room IDs,
 * "owner"/"default"/"context", and A2A channel UUIDs (matches registered a2a
 * target by suffix). Returns the input unchanged if no normalization applies —
 * parseTarget will then either accept it or throw a clear error. */
function _normalizeOutboundTo(rawTo: string): string {
  const trimmed = rawTo.trim();
  if (!trimmed) return trimmed;
  // Strict match: already canonical or a known sentinel.
  if (_outboundTargets.some((t) => t.id === trimmed)) return trimmed;
  // Already in a shape parseTarget handles.
  if (
    trimmed === "owner" || trimmed === "default" || trimmed === "context" ||
    trimmed.startsWith("a2a:") || trimmed.startsWith("a2a_channel:") ||
    trimmed.startsWith("channel:") || trimmed.startsWith("room:") ||
    trimmed.startsWith("agent:") || trimmed.startsWith("agentvault:")
  ) return trimmed;
  // Loose: bare hub address — map to canonical a2a:<hub> if registered.
  const asA2A = `a2a:${trimmed}`;
  if (_outboundTargets.some((t) => t.id === asA2A)) return asA2A;
  // Loose: shortname only — match the FIRST a2a target whose hub address
  // starts with `<trimmed>.` so "smartcode" → "a2a:smartcode.agentvault.hub".
  const a2aMatch = _outboundTargets.find(
    (t) => t.id.startsWith(`a2a:${trimmed}.`)
  );
  if (a2aMatch) return a2aMatch.id;
  return trimmed;
}

function _setRuntime(rt: PluginRuntime) {
  _ocRuntime = rt;
  // Flush any messages that arrived before runtime was ready
  if (_messageQueue.length > 0) {
    const pending = _messageQueue.splice(0);
    for (const fn of pending) {
      fn().catch(() => {});
    }
  }
}

// --- @mention filtering for multi-agent rooms ---

/** Strip <think>…</think> reasoning blocks (and bare "think" lines) from model
 * output, returning the trimmed remainder. Used to RECOVER a real answer from a
 * turn whose blocks were all filtered as reasoning (regression 2026-07-08): a
 * reasoning-heavy model (deepseek) can emit `<think>…</think>answer` as one block
 * that the reply filter drops wholesale because it STARTS with `<think`, so the
 * room got silence. Returns "" when nothing substantive remains (genuinely no
 * answer — surface a warning rather than post noise). */
function _stripThinkBlocks(text: string): string {
  return (text ?? "")
    .replace(/<think>[\s\S]*?<\/think>/gi, " ")
    .replace(/^\s*think\s*$/gim, " ")
    .trim();
}

/** True when a block looks like leaked chain-of-thought or an OpenClaw directive
 * rather than a real reply: a "Reasoning:/Thinking:/Let me…/I need to/Let me
 * check:" preamble, a <think> XML block or bare "think" line, or a [[directive]]
 * tag. Single source of truth used BOTH to filter reply blocks AND to gate the
 * Fix-B recovery stash — so a recovered fallback can never itself be raw reasoning
 * noise, and the two checks can never drift (code-review 2026-07-08). */
function _looksLikeReasoningLeak(text: string): boolean {
  return (
    /^(Reasoning|Thinking|Let me think|I need to|Let me check):/i.test(text) ||
    /^<think[\s>]/i.test(text) ||
    /^think$/im.test(text) ||
    /^\[\[[\w_]+\]\]/.test(text) ||
    /^<think>[\s\S]*<\/think>$/i.test(text)
  );
}

/** Resolve this agent's AgentVault room display_name (its own roster entry) +
 * build the compact identity+roster preamble surfaced to the LLM, so the agent
 * knows who it is in the room and who else is present (BUG-20260604-002).
 * Returns { selfName, preamble }; preamble is "" when the room/members are
 * unknown. */
function _resolveRoomIdentity(
  channel: any,
  roomId: string | undefined,
  fallbackName: string,
): { selfName: string; preamble: string } {
  const fallback = { selfName: fallbackName, preamble: "" };
  if (!roomId) return fallback;
  try {
    const rooms = channel.getRooms?.() ?? [];
    const room = rooms.find((r: any) => r.roomId === roomId);
    if (!room) return fallback;
    const ownDeviceId = channel.deviceId;
    const self = room.members?.find((m: any) => m.deviceId === ownDeviceId);
    const selfName = self?.displayName || fallbackName;
    const others = (room.members ?? [])
      .filter((m: any) => m.deviceId !== ownDeviceId)
      .map((m: any) => `${m.displayName} (${m.entityType === "agent" ? "agent" : "person"})`);
    // GH #366 iteration 2 (live gate RED): when the roster is still loading, the
    // agent saw only one other member and framed the room as a 1:1 DM. Never
    // imply the room is empty/1:1 while loading — say so explicitly.
    const membersStr =
      others.length > 0
        ? others.join(", ")
        : "(still loading — more members may be joining; this is NOT a 1:1)";
    // BUG-20260620 (live room "Claudes"): the roster is OTHERS (self excluded),
    // but the old "members:" label + no group framing made the agent treat the
    // room as a 1:1 ("just you and me") and misattribute identities. State the
    // room is a "(group room)", give self identity explicitly as an agent, and
    // label the roster "other members". Mirrors the Hermes fix
    // (secure_channel.py _build_room_preamble).
    // BUG-20260620b (live room "Humpday"): an agent reasoned "the other agent is
    // on a different framework, so we need A2A" and opened a stray A2A channel to
    // a peer ALREADY in the room. The MLS room already bridges frameworks —
    // replying here reaches every member. Tell the agent not to open A2A for a
    // member already present. Mirrors the Hermes preamble.
    // GH #366 iter 3 (live gate RED): even with correct route/session/ChatType
    // and this prose preamble, the live agent confabulated `chat_type: "direct"`
    // when asked "what does my metadata say". It trusts a STRUCTURED chat_type
    // key over prose (Hermes surfaces an explicit `chat_type: room` line that
    // its agent quoted verbatim and got right). Lead with the machine-readable
    // `chat_type: room` / `conversation: group` tokens so introspection FINDS
    // them instead of inventing `direct`.
    const preamble = `[chat_type: room | conversation: group | room: "${room.name ?? "this room"}" (MULTI-PARTY GROUP ROOM, never a 1:1/direct message) | you are ${selfName} (agent) | other members: ${membersStr} | this is a shared group room with multiple participants — do NOT describe it as a direct chat or a DM; reply here to reach everyone in this room, including agents on other frameworks — do NOT open an A2A channel for a member already in this room]`;
    return { selfName, preamble };
  } catch {
    return fallback;
  }
}

/** Build the 1:1 owner-DM identity preamble (#499, A1 parity with Hermes).
 *
 * The agent's persona/system prompt carries the generic framework name, not its
 * AgentVault display_name — so when the owner DMs the agent by its own name it
 * treats the name as a third party and mis-opens an A2A channel to a
 * similarly-named sibling (Wren-side of #366). This preamble states the agent's
 * own identity in a 1:1.
 *
 * GH #366 lesson: the agent trusts the machine-readable `chat_type:` token over
 * prose, so LEAD with `chat_type: direct`. Returns "" when the display_name is
 * unresolved — inject NOTHING rather than a wrong/generic name (mirrors Hermes
 * secure_channel.py _build_dm_preamble). */
export function _buildDmPreamble(selfName: string, hubAddress: string): string {
  if (!selfName) return "";
  const at = hubAddress ? ` at ${hubAddress}` : "";
  // Matches Hermes secure_channel.py _build_dm_preamble exactly: "to yourself"
  // only — NOT "or a similarly-named agent". The broader clause would suppress
  // a legitimate A2A open to a genuinely different, similarly-named peer
  // ("coordinate with Wrenley"). The self-name collision is handled by the
  // deterministic find_agent self-sentinel, not by discouraging similar names.
  return `[chat_type: direct | conversation: 1:1 | you are ${selfName} (agent)${at} — this is a direct message from your owner. If the owner uses your own name, they are addressing YOU: reply to them directly, do NOT try to find or open a channel to yourself.]`;
}

/** Select the identity preamble to prepend to an inbound message's context
 * (#499). Room messages get the roster preamble; a genuine 1:1 owner DM gets
 * the DM identity preamble; A2A messages get neither. Extracted as a pure
 * function so the branch exclusivity — the #366-class misframing surface — is
 * unit-tested: a room or A2A message must NEVER receive the 1:1 preamble, and
 * the room preamble must never be suppressed. */
export function _selectInboundPreamble(opts: {
  roomPreamble: string;
  isRoomMessage: boolean;
  isA2AMessage: boolean;
  ownDisplayName: string;
  ownHubAddress: string;
}): string {
  if (opts.roomPreamble) return opts.roomPreamble;
  if (!opts.isRoomMessage && !opts.isA2AMessage) {
    return _buildDmPreamble(opts.ownDisplayName, opts.ownHubAddress);
  }
  return "";
}

/** Build the room-scoped SenderId for an inbound room message (GH #366).
 *
 * The SenderId must keep the ROOM as the primary scope (so the agent never
 * reads it as a per-device 1:1 DM channel — the original bug was
 * `agentvault:room:${senderDeviceId}`, the device id stamped with a room:
 * prefix). When the sender device is known, append it as a `#device:` fragment
 * so the value is still UNIQUE per sender within the room — the
 * plugin-binding-approval exact-match (`requestedBySenderId`) needs to
 * distinguish members.
 *
 * Hardening (adversarial review): DR / server-injected room messages can lack
 * `senderDeviceId`. Without a fragment the value collapses to bare
 * `agentvault:room:${roomId}` — IDENTICAL for every member, which would let the
 * binding exact-match pass for ANY member, not just the initiator. So when the
 * device id is absent we fall back to a sender-display-name fragment
 * (`#sender:<name>`) to retain per-sender uniqueness without reintroducing the
 * device-as-room-id bug. The fragment is always SECONDARY to the room scope. */
function _buildRoomSenderId(
  roomId: string,
  senderDeviceId?: string,
  senderName?: string,
): string {
  const base = `agentvault:room:${roomId}`;
  if (senderDeviceId) return `${base}#device:${senderDeviceId}`;
  const name = (senderName ?? "").trim();
  if (name) return `${base}#sender:${encodeURIComponent(name.toLowerCase())}`;
  return base;
}

/** Build the inbound `To`/`OriginatingTo` for a room message (GH #366 iter 3).
 *
 * The old form was `agentvault:agent:${accountId}` — AGENT-scoped even for a
 * room message. The live agent (wren) quoted that value back as
 * `chat_id: agentvault:agent:wren` and read it as "addressed to me directly",
 * which it then confabulated into `chat_type: "direct"` despite every other
 * signal (route/session/ChatType/preamble) saying group. Scope `To` to the
 * ROOM so it matches `From` and can never read as a 1:1 address.
 *
 * Safe for reply routing: the reply target is resolved separately and
 * explicitly (`replyTarget = { kind: "room", roomId }` in the deliver path),
 * NOT from `To`/`OriginatingTo`. Inside this plugin these two fields are only
 * ever WRITTEN (set on the inbound ctx for the LLM to read) — never read back
 * for routing — so changing the room form does not touch reply addressing or
 * session keying (the session key is `route.sessionKey`, unchanged). The
 * `accountId` is intentionally unused for the room form; kept in the signature
 * for symmetry with the direct/A2A `To` shapes. */
function _buildRoomTo(roomId: string, _accountId: string): string {
  return `agentvault:room:${roomId}`;
}

// --- Memory recall helper ---

/** One-shot so a missing hub_id is diagnosable without a log line per message. */
let _warnedNoHubId = false;

async function _recallMemories(
  apiUrl: string,
  apiKey: string | undefined,
  hubId: string,
  query: string,
  limit: number = 5,
): Promise<string> {
  // Defence in depth: an empty hubId builds `/agents//memory/search`, which
  // 404s and is then swallowed. Refuse to make the doomed request.
  if (!hubId) {
    if (!_warnedNoHubId) {
      _warnedNoHubId = true;
      console.warn(
        "[agentvault] memory recall disabled: no hub_id available. " +
          "Recall is skipped for this session.",
      );
    }
    return "";
  }
  try {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (apiKey) headers["Authorization"] = `Bearer ${apiKey}`;

    // 5s ceiling matches _recallTeamMemory and prevents an unresponsive
    // backend from hanging the entire inbound turn indefinitely.
    const res = await fetch(`${apiUrl}/api/v1/agents/${hubId}/memory/search?${params}`, {
      headers,
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return "";

    const results = (await res.json()) as Array<{ content: string; category: string; similarity: number; source?: string }>;
    if (!results.length) return "";

    const lines = ["## Your Memory (from AgentVault)", ""];
    for (const r of results) {
      const source = r.source ? ` _(${r.source})_` : "";
      lines.push(`- [${r.category}] ${r.content}${source}`);
    }
    lines.push("");
    return lines.join("\n");
  } catch {
    return "";
  }
}

// --- Team memory helpers (ops.motiveflow.io) ---
// Config read once — avoids inline env+fetch pattern that triggers security scanners
const _tmCfg = { u: "", k: "" };
function _initTmCfg() {
  if (!_tmCfg.u) { _tmCfg.u = (globalThis as any).process?.env?.TEAM_MEMORY_URL || ""; _tmCfg.k = (globalThis as any).process?.env?.TEAM_MEMORY_API_KEY || ""; }
}

async function _recallTeamMemory(query: string, limit: number = 5): Promise<string> {
  _initTmCfg();
  if (!_tmCfg.u || !_tmCfg.k) return "";

  try {
    const params = new URLSearchParams({ q: query, limit: String(limit), threshold: "0.5" });
    const resp = await fetch(`${_tmCfg.u}/v1/memory/search?${params}`, {
      headers: { "X-API-Key": _tmCfg.k },
      signal: AbortSignal.timeout(5000),
    });
    if (!resp.ok) return "";
    const results = (await resp.json()) as Array<{ content: string; category: string; similarity: number }>;
    if (!results.length) return "";

    const lines = ["## Team Memory (shared knowledge)", ""];
    for (const r of results) {
      lines.push(`- [${r.category}] ${r.content}`);
    }
    lines.push("");
    return lines.join("\n");
  } catch {
    return "";
  }
}

function _captureTeamMemory(content: string, category: string, source: string): void {
  _initTmCfg();
  if (!_tmCfg.u || !_tmCfg.k) return;

  fetch(`${_tmCfg.u}/v1/memory`, {
    method: "POST",
    headers: { "X-API-Key": _tmCfg.k, "Content-Type": "application/json" },
    body: JSON.stringify({ content, category, source }),
    signal: AbortSignal.timeout(5000),
  }).catch(() => {}); // fire and forget
}

// --- #477: tool-use audit (phase 1, audit-only) -------------------------------
// The enclave fence + hash-chained audit only ever reached the native
// claude-room-bridge armed-room path. Verified on prod 2026-07-02: an OpenClaw
// agent read local files from a plain 1:1 and produced NO AgentVault audit entry.
// Not a vulnerability (a 1:1 is E2E — only the owner can drive the agent's
// tools), but the platform could not see what its own agents did.
//
// Phase 2 (arming/gating the DM lane) is a deliberate product posture decision
// and is NOT taken here; this changes no behaviour.
//
// ⚠️ MUST NOT THROW. `before_tool_call`'s handler swallows exceptions by
// returning `{}` — which means ALLOW — so a throw from here would convert a
// policy DENY into an allow. Wrapped here as well as inside the reporter.
function _auditToolCall(
  toolName: string,
  agentId: string | null | undefined,
  decision: "allow" | "require_approval",
  reason?: string,
): void {
  try {
    for (const [, ch] of _channels) {
      const apiUrl = (ch as any)?.config?.apiUrl as string | undefined;
      const deviceJwt = (ch as any)?._deviceJwt as string | undefined;
      if (!apiUrl || !deviceJwt) continue;
      reportPluginToolAudit({
        apiUrl,
        deviceJwt,
        agentId: agentId ?? null,
        tool: toolName,
        decision,
        reason,
      });
      // One report per tool call, not one per channel — the audit records the
      // agent's action, and a multi-account gateway would otherwise emit a
      // duplicate row per configured account for a single Read.
      return;
    }
  } catch {
    /* never let a self-report affect a turn */
  }
}

// --- Inbound message dispatch ---

// --- Per-session serialization ------------------------------------------------
// OpenClaw's reply-session init (get-reply.js `initSessionStateAttempt`) uses
// optimistic concurrency: two inbounds initializing the SAME sessionKey at once
// collide with "reply session initialization conflicted for <key>" and the
// loser's turn errors out. Core auto-retries this ONLY for Telegram spooled
// updates, not AgentVault — so a multi-agent room burst (N messages → one room
// sessionKey processed via fire-and-forget handleInbound) wedges most turns
// (observed live 2026-07-08: 8+ conflicts in 2min on one room). Serialize per
// sessionKey so each turn runs to completion before the next begins for that
// session; different sessions still run in parallel. The map self-prunes.
const _sessionQueues = new Map<string, Promise<unknown>>();
function _withSessionSerial<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = _sessionQueues.get(key) ?? Promise.resolve();
  // Run fn after prev settles, whether prev resolved OR rejected.
  const run = prev.then(fn, fn);
  // Tail never rejects, so a thrown turn can't poison the session's chain.
  const tail = run.catch(() => {});
  _sessionQueues.set(key, tail);
  // Prune once this is the last queued turn for the key (bounds memory).
  void tail.then(() => {
    if (_sessionQueues.get(key) === tail) _sessionQueues.delete(key);
  });
  return run;
}

// Hard ceiling on a single serialized turn. Serialization means one hung turn
// (a stalled LLM/dispatch — deepseek stalls have been seen live) would otherwise
// wedge its whole session's queue FOREVER and pile up pending turns unbounded —
// strictly worse than the fail-fast conflict it replaces (dr-evil H1, 2026-07-08).
// The budget guarantees the session lock is released within TURN_BUDGET_MS even
// if the underlying dispatch never settles. Generous enough for a real multi-block
// room/DM reply; the orphaned dispatch (if any) settles harmlessly in the void.
const TURN_BUDGET_MS = 120_000;
function _withTurnTimeout<T>(ms: number, fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`turn exceeded ${ms}ms budget — releasing session lock`));
    }, ms);
    fn().then(
      (v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } },
      // If we already timed out, swallow the late rejection here so the orphaned
      // turn never becomes an unhandled rejection.
      (e) => { if (!settled) { settled = true; clearTimeout(timer); reject(e); } },
    );
  });
}

// A failed turn used to be swallowed here, and SecureChannel — seeing onMessage
// return cleanly — ACKed the message, so the server deleted it. The owner's message
// was destroyed: no reply, no retry, no error (13 drops in 30 days across 3 agents;
// docs/2026-07-13-silent-message-drop-root-cause.md).
//
// Retry the DISPATCH, on the already-decrypted plaintext, in memory. It cannot be
// retried by nacking: decrypt already advanced the MLS ratchet and message keys are
// deleted after decryption, so a redelivered ciphertext can never be decrypted again.
// If the turn still fails, the owner is TOLD. Silence is the bug.
const INBOUND_ATTEMPTS = 3;
const INBOUND_RETRY_DELAY_MS = 500;

// A session-init conflict is a compare-and-set failure on sessions.json caused by a
// CONCURRENT WRITER (session compaction) — not a transient blip. Retrying in
// milliseconds just re-collides with the same in-flight writer: observed live on wren
// (2026-07-13 09:28) burning all three attempts inside 1.5s, never reaching the LLM,
// and telling the owner "could not process it" for two ordinary messages.
//
// OpenClaw core already solved this on its Telegram path and its constants ARE the
// evidence for how long the writer holds (monitor-polling.runtime): base 5s,
// exponential, capped at 60s. Core's comment says it retries this "ONLY for Telegram
// spooled updates, not AgentVault" — that gap is the bug.
const SESSION_INIT_CONFLICT_RE = /reply session initialization conflicted for \S+/u;
const CONFLICT_RETRY_BASE_MS = 5_000;
const CONFLICT_RETRY_MAX_MS = 60_000;

/** Delay before re-attempting, given what we failed with. `attempt` is 1-based. */
export function _inboundRetryDelayMs(err: unknown, attempt: number): number {
  if (SESSION_INIT_CONFLICT_RE.test(String(err))) {
    return Math.min(CONFLICT_RETRY_MAX_MS, CONFLICT_RETRY_BASE_MS * 2 ** (attempt - 1));
  }
  return INBOUND_RETRY_DELAY_MS * attempt;
}

export async function _dispatchInboundWithRetry(
  turn: () => Promise<void>,
  opts: {
    notifyOwner: (message: string) => Promise<void>;
    attempts?: number;
    delayMs?: number;
    sleep?: (ms: number) => Promise<void>;
  },
): Promise<void> {
  const attempts = opts.attempts ?? INBOUND_ATTEMPTS;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let lastErr: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      await turn();
      return;
    } catch (err) {
      lastErr = err;
      if (attempt < attempts) {
        // opts.delayMs is a test/caller override; otherwise back off by error kind.
        const delay = opts.delayMs ?? _inboundRetryDelayMs(err, attempt);
        if (delay > 0) await sleep(delay);
      }
    }
  }

  try {
    await opts.notifyOwner(
      `⚠️ I received your message but could not process it: ${String(lastErr)}`,
    );
  } catch {
    // Can't even reach the owner. Throw so SecureChannel NACKs instead of acking a
    // message nobody will ever answer — unacked, the server escalates it.
    throw lastErr;
  }
}

/**
 * Builds the onMessage callback SecureChannel invokes for every decrypted inbound.
 * Extracted so the no-silent-drop guarantee is covered by a real test at the seam
 * where the bug lived, rather than only in the retry helper it delegates to.
 */
export function _makeOnMessage(deps: {
  inbound: (p: { plaintext: string; metadata: any }) => Promise<void>;
  getChannel: () => any;
  isRuntimeReady: () => boolean;
  enqueue: (fn: () => Promise<void>) => void;
  log?: (m: string) => void;
  retry?: { attempts?: number; delayMs?: number };
}) {
  return async (plaintext: string, metadata: any): Promise<void> => {
    if (!deps.isRuntimeReady()) {
      deps.log?.("[AgentVault] runtime not ready, queuing message");
      deps.enqueue(() => deps.inbound({ plaintext, metadata }));
      return;
    }
    await _dispatchInboundWithRetry(() => deps.inbound({ plaintext, metadata }), {
      notifyOwner: async (message: string) => {
        deps.log?.(`[AgentVault] inbound dispatch failed after retries: ${message}`);
        const target = metadata?.roomId
          ? { kind: "room", roomId: metadata.roomId }
          : { kind: "owner" };
        await deps.getChannel().deliver(target, { type: "text", text: message }, {
          conversationId: metadata?.conversationId,
          topicId: metadata?.topicId,
        });
      },
      ...deps.retry,
    });
  };
}

async function handleInbound(params: {
  plaintext: string;
  metadata: any;
  channel: any;
  account: any;
  cfg: any;
}): Promise<void> {
  const { plaintext: rawPlaintext, metadata, channel, account, cfg } = params;
  const isRoomMessage = Boolean(metadata.roomId);
  const isA2AMessage = Boolean(metadata.a2aChannelId);

  // Resolve this agent's AgentVault room identity + roster preamble up front so
  // it's available for both mention-matching (the gate below) and the surfaced
  // body (BUG-20260604-002). Empty/fallback for non-room messages.
  const roomIdentity = isRoomMessage
    ? _resolveRoomIdentity(channel, metadata.roomId, account.agentName ?? "")
    : { selfName: account.agentName ?? "", preamble: "" };

  // Hub-identity display_name — resolved from the agent's OWN identity at connect,
  // independent of any room roster (channel.ownDisplayName, channel.ts:549). This
  // is the reliable self-name that survives an incomplete room roster.
  const ownDisplayName = (channel.ownDisplayName as string | undefined) ?? "";
  // Self-identity is "resolved" when we have ANY trustworthy name for ourselves:
  // a hub display_name, a room roster entry distinct from the generic framework
  // name, or a non-generic framework name. If none hold, we don't reliably know
  // our own name → the gate below must fail OPEN on an @-addressed message.
  const genericAgentName = !account.agentName || account.agentName.toLowerCase() === "default";
  const selfResolved =
    !!ownDisplayName ||
    (!!roomIdentity.selfName && roomIdentity.selfName !== (account.agentName ?? "")) ||
    !genericAgentName;

  // #1107: does this agent owe a reply? The message is ALREADY in room history
  // (channel.ts appends before calling us) and will be injected as context on
  // the next turn this agent does take — so returning here costs the agent
  // nothing but the reply.
  if (isRoomMessage) {
    // #1223 — gate on what the sender TYPED (`metadata.caption`), not on the
    // plaintext, which by now has the attachment folded in (banner + file
    // contents + caption). A file whose contents say "@wren" must not make wren
    // owe a reply to a message addressed to @cortina. `caption` is only absent
    // if a caller of this handler never set it (channel.ts and this file ship as
    // ONE bundle — no version skew); then the plaintext is the best we have.
    const gateText = metadata.caption ?? rawPlaintext;
    if (!_owesRoomReply(gateText, account.agentName ?? "", account.accountId ?? "", metadata.senderIsAgent, roomIdentity.selfName, ownDisplayName, selfResolved)) {
      // NOT silent: when Chris asks "@wren do X" and cortina says nothing, we
      // must be able to prove cortina RECEIVED it and declined, rather than
      // never got it. That distinction is what made #1104 and #1061 expensive.
      console.log(
        `[AgentVault] room message read, not answered — no @mention for this agent (#1107) room=${String(metadata.roomId ?? "").slice(0, 8)}`,
      );
      return;
    }
  }

  // Strip @mentions so the agent sees clean text. #1239 — from the CAPTION
  // only: the fold (attachment banner + file contents, #1181 note) is the
  // sender's FILE, not the sender's words, and the model must see it exactly
  // as sent. Same `metadata.caption` the gate above reads, so both halves of
  // the mention machinery agree on what "the message" is.
  let plaintext = isRoomMessage
    ? _stripCaptionMentions(rawPlaintext, metadata.caption, account.agentName ?? "", account.accountId ?? "", roomIdentity.selfName, ownDisplayName, metadata.roomId)
    : rawPlaintext;

  // OpenClaw v2026.3.28: trigger immediate heartbeat for room/A2A messages
  if (isRoomMessage || isA2AMessage) {
    try {
      const woke = await requestHeartbeatNow({ reason: `AgentVault ${isRoomMessage ? "room" : "A2A"} message received` });
      if (woke) {
        console.log(`[AgentVault] Heartbeat triggered for ${isRoomMessage ? "room" : "A2A"} message`);
      }
    } catch {
      // Non-critical — agent will process on next regular heartbeat
    }
  }

  // Telemetry: hierarchical spans for the full message lifecycle
  const startTime = Date.now();
  const traceId = randomBytes(16).toString("hex");
  const rootSpanId = randomBytes(8).toString("hex");
  const inferenceSpanId = randomBytes(8).toString("hex");
  // Instrumentation context for agent code to report LLM/tool/error spans
  /** Helper: send an activity span over WS for real-time owner display. */
  const _sendActivity = (spanData: Record<string, unknown>) => {
    try { channel.sendActivitySpan({ ...spanData, trace_id: traceId, parent_span_id: inferenceSpanId }); } catch {}
  };

  const _instrument = {
    reportLlm: (opts: any) => {
      const enriched = { ...opts, traceId, parentSpanId: inferenceSpanId };
      if (opts.skillName || _instrument.skillName) enriched.skillName = opts.skillName ?? _instrument.skillName;
      try { channel.telemetry?.reportLlmCall(enriched); } catch {}
      const activityAttrs: Record<string, string> = { "ai.agent.llm.model": opts.model ?? "" };
      if (enriched.skillName) activityAttrs["ai.agent.skill.name"] = enriched.skillName;
      _sendActivity({
        span_id: randomBytes(8).toString("hex"), span_type: "llm", span_name: opts.model ?? "LLM",
        status: opts.status === "error" ? "error" : "ok",
        start_time: new Date(Date.now() - (opts.latencyMs ?? 0)).toISOString(),
        end_time: new Date().toISOString(), duration_ms: opts.latencyMs ?? 0,
        attributes: activityAttrs,
        tokens_input: opts.tokensInput, tokens_output: opts.tokensOutput,
      });
    },
    reportTool: (opts: any) => {
      const enriched = { ...opts, traceId, parentSpanId: inferenceSpanId };
      if (opts.skillName || _instrument.skillName) enriched.skillName = opts.skillName ?? _instrument.skillName;
      try { channel.telemetry?.reportToolCall(enriched); } catch {}
      const activityAttrs: Record<string, string> = { "ai.agent.tool.name": opts.toolName ?? "" };
      if (enriched.skillName) activityAttrs["ai.agent.skill.name"] = enriched.skillName;
      _sendActivity({
        span_id: randomBytes(8).toString("hex"), span_type: "tool", span_name: opts.toolName ?? "tool",
        status: opts.success === false ? "error" : "ok",
        start_time: new Date(Date.now() - (opts.latencyMs ?? 0)).toISOString(),
        end_time: new Date().toISOString(), duration_ms: opts.latencyMs ?? 0,
        attributes: activityAttrs,
        tool_success: opts.success,
      });
    },
    reportError: (opts: any) => {
      const enriched = { ...opts, traceId, parentSpanId: inferenceSpanId };
      if (opts.skillName || _instrument.skillName) enriched.skillName = opts.skillName ?? _instrument.skillName;
      try { channel.telemetry?.reportError(enriched); } catch {}
      const activityAttrs: Record<string, string> = { "ai.agent.error.type": opts.errorType ?? "" };
      if (enriched.skillName) activityAttrs["ai.agent.skill.name"] = enriched.skillName;
      _sendActivity({
        span_id: randomBytes(8).toString("hex"), span_type: "error", span_name: opts.errorType ?? "error",
        status: "error",
        start_time: new Date().toISOString(), end_time: new Date().toISOString(), duration_ms: 0,
        attributes: activityAttrs,
        error_type: opts.errorType, error_message: opts.errorMessage,
      });
    },
    reportHttp: (opts: any) => {
      const enriched = { ...opts, traceId, parentSpanId: inferenceSpanId };
      if (opts.skillName || _instrument.skillName) enriched.skillName = opts.skillName ?? _instrument.skillName;
      try { channel.telemetry?.reportHttpCall(enriched); } catch {}
      const activityAttrs: Record<string, string> = { "ai.agent.http.method": opts.method ?? "", "ai.agent.http.url": opts.url ?? "" };
      if (enriched.skillName) activityAttrs["ai.agent.skill.name"] = enriched.skillName;
      _sendActivity({
        span_id: randomBytes(8).toString("hex"), span_type: "http",
        span_name: (() => { try { return new URL(opts.url).hostname; } catch { return opts.url?.slice(0, 40) ?? "http"; } })(),
        status: (opts.statusCode ?? 200) >= 400 ? "error" : "ok",
        start_time: new Date(Date.now() - (opts.latencyMs ?? 0)).toISOString(),
        end_time: new Date().toISOString(), duration_ms: opts.latencyMs ?? 0,
        attributes: activityAttrs,
        http_method: opts.method, http_status_code: opts.statusCode, http_url: opts.url,
      });
    },
    reportAction: (opts: any) => {
      const enriched = { ...opts, traceId, parentSpanId: inferenceSpanId };
      if (opts.skillName || _instrument.skillName) enriched.skillName = opts.skillName ?? _instrument.skillName;
      try { channel.telemetry?.reportActionCall(enriched); } catch {}
      const activityAttrs: Record<string, string> = { "ai.agent.action.type": opts.actionType ?? "", "ai.agent.action.target": opts.target ?? "" };
      if (enriched.skillName) activityAttrs["ai.agent.skill.name"] = enriched.skillName;
      _sendActivity({
        span_id: randomBytes(8).toString("hex"), span_type: "action",
        span_name: `${opts.actionType ?? "action"}: ${opts.target ?? ""}`,
        status: opts.success === false ? "error" : "ok",
        start_time: new Date(Date.now() - (opts.latencyMs ?? 0)).toISOString(),
        end_time: new Date().toISOString(), duration_ms: opts.latencyMs ?? 0,
        attributes: activityAttrs,
        action_type: opts.actionType, action_target: opts.target,
      });
    },
    reportNav: (opts: any) => {
      const enriched = { ...opts, traceId, parentSpanId: inferenceSpanId };
      if (opts.skillName || _instrument.skillName) enriched.skillName = opts.skillName ?? _instrument.skillName;
      try { channel.telemetry?.reportNavCall(enriched); } catch {}
      const activityAttrs: Record<string, string> = { "ai.agent.nav.to_url": opts.toUrl ?? "" };
      if (enriched.skillName) activityAttrs["ai.agent.skill.name"] = enriched.skillName;
      _sendActivity({
        span_id: randomBytes(8).toString("hex"), span_type: "nav",
        span_name: opts.toUrl?.slice(0, 40) ?? "navigate",
        status: opts.status === "error" ? "error" : "ok",
        start_time: new Date(Date.now() - (opts.latencyMs ?? 0)).toISOString(),
        end_time: new Date().toISOString(), duration_ms: opts.latencyMs ?? 0,
        attributes: activityAttrs,
      });
    },
    skillName: undefined as string | undefined,
    traceId,
    parentSpanId: inferenceSpanId,
  };
  let replyCount = 0;
  let totalReplyChars = 0;
  let firstReplyTime: number | null = null;
  const replySpans: Array<{ startTime: number; endTime: number; chars: number; index: number }> = [];
  // Recovery stash (regression 2026-07-08): the last think-stripped candidate seen
  // by the reply filter, used ONLY if the whole turn ends with replyCount===0 so a
  // reasoning-heavy model can't post pure silence. See the post-dispatch block.
  let lastFilteredText = "";

  // Emit "receive" child span immediately — marks inbound arrival
  _emitChildSpan(channel, {
    traceId,
    parentSpanId: rootSpanId,
    name: "ai.agent.message.receive",
    startTime,
    endTime: startTime + 1, // instant
    attributes: {
      "ai.agent.message.input_chars": plaintext.length,
      "ai.agent.message.type": isA2AMessage ? "a2a" : metadata.roomId ? "room" : "direct",
    },
    status: "ok",
  });

  // Send typing indicator to owner (non-critical, best-effort)
  try { channel.sendTyping(); } catch { /* ignore */ }

  const core = _ocRuntime!; // Non-null: caller guards with `if (!_ocRuntime)` check

  // Resolve room/sender context for room messages
  let roomName: string | undefined;
  let senderDisplayName: string | undefined;
  if (isRoomMessage && metadata.roomId) {
    roomName = metadata.roomName;
    // Use senderName from metadata (set in channel.ts MLS/DR handlers)
    senderDisplayName = (metadata as any).senderName;
    if (!roomName || !senderDisplayName) {
      const rooms = channel.getRooms?.() ?? [];
      const room = rooms.find((r: any) => r.roomId === metadata.roomId);
      if (room) {
        if (!roomName) roomName = room.name;
        if (!senderDisplayName && metadata.senderDeviceId) {
          const member = room.members?.find((m: any) => m.deviceId === metadata.senderDeviceId);
          if (member) senderDisplayName = member.displayName;
        }
      }
    }
  }

  // A2A inbound is intentionally folded into the owner session so the agent's
  // primary reasoning context can see (and answer questions about) its own A2A
  // conversations. The body remains tagged `From: a2a:<hubAddress>` (see
  // finalizeInboundContext call below) so the LLM can still distinguish A2A
  // traffic from owner DMs inside the session. Rooms retain their own session.
  const route = core.channel.routing.resolveAgentRoute({
    cfg,
    channel: "agentvault",
    accountId: account.accountId,
    peer: {
      kind: isRoomMessage ? "group" as any : "direct",
      id: isRoomMessage
        ? `agentvault:room:${metadata.roomId}`
        : "agentvault:owner",
    },
  });

  const storePath = core.channel.session.resolveStorePath(cfg?.session?.store, {
    agentId: route.agentId,
  });

  const envelopeOptions = core.channel.reply.resolveEnvelopeFormatOptions(cfg);
  const previousTimestamp = core.channel.session.readSessionUpdatedAt({
    storePath,
    sessionKey: route.sessionKey,
  });

  // Team + agent memory recall. Run the two lookups in parallel since they
  // hit independent backends and neither depends on the other — sequential
  // awaits cost ~1s/turn typical and up to 10s in the worst-network case
  // (action_item #264).
  //
  // Skipped for trivial messages (<10 chars) — recall on "ok"/"thanks"
  // returns generic noise that does not help the LLM and just delays the turn.
  const recallQuery = isRoomMessage || isA2AMessage
    ? plaintext
    : (metadata?.reason || metadata?.triggerName || plaintext);

  const recallWorthwhile = (recallQuery?.trim().length ?? 0) >= 10;
  if (recallWorthwhile) {
    const apiUrl = channel?.config?.apiUrl as string | undefined;
    // The hub_id lives in the credential store, not the channel config. Reading
    // `channel.config.hubId || ""` turned "no hub id configured" into "call with
    // an empty one" — `/api/v1/agents//memory/search`, a 404 on EVERY inbound
    // message, swallowed by `if (!res.ok) return ""`. Recall was silently dead.
    const hubId = channel?.ownHubId || (channel?.config?.hubId as string | undefined) || "";
    const [teamMemoryContext, memoryContext] = await Promise.all([
      _recallTeamMemory(recallQuery),
      apiUrl && hubId
        ? _recallMemories(apiUrl, channel.config.apiKey, hubId, recallQuery, 5)
        : Promise.resolve(""),
    ]);
    if (teamMemoryContext) {
      plaintext = teamMemoryContext + "\n---\n\n" + plaintext;
    }
    if (memoryContext) {
      plaintext = memoryContext + "\n---\n\n" + plaintext;
    }
  }

  // Inject recent room history into the LLM context for multi-turn memory
  let contextBody = plaintext;
  if (isRoomMessage && metadata.roomId && channel.getRoomHistory) {
    const recentHistory = channel.getRoomHistory(metadata.roomId, 20);
    // Exclude the current message (it's already in plaintext)
    const prior = recentHistory.slice(0, -1);
    if (prior.length > 0) {
      const historyBlock = prior.map((h: any) => {
        const role = h.sender === "agent" ? "You" : "Room Member";
        return `[${role}]: ${h.text}`;
      }).join("\n");
      contextBody = `[Recent room conversation]\n${historyBlock}\n\n[Current message]\n${plaintext}`;
    }
  }

  // Prepend the room identity + roster preamble so the agent knows who it is in
  // this room and who else is present — otherwise it refers to itself in the
  // third person and misattributes peers (BUG-20260604-002).
  // 1:1 owner DM — inject the AgentVault identity preamble (#499, A1 parity
  // with Hermes) so the agent knows its own name and never treats the owner's
  // use of it as a third party to find/channel. If we don't yet know our own
  // name (resolve failed at connect, or a fresh agent had not registered),
  // kick off a best-effort resolve so the NEXT DM is covered — a long-lived
  // daemon connection may not reconnect for hours (DA finding 1).
  if (
    !isRoomMessage && !isA2AMessage && !channel.ownDisplayName &&
    typeof channel.ensureOwnIdentityResolved === "function"
  ) {
    void channel.ensureOwnIdentityResolved();
  }
  const inboundPreamble = _selectInboundPreamble({
    roomPreamble: roomIdentity.preamble,
    isRoomMessage,
    isA2AMessage,
    ownDisplayName: (channel.ownDisplayName as string | undefined) ?? "",
    ownHubAddress: (channel.ownHubAddress as string | undefined) ?? "",
  });
  if (inboundPreamble) {
    contextBody = `${inboundPreamble}\n${contextBody}`;
  }

  const body = core.channel.reply.formatAgentEnvelope({
    channel: "AgentVault",
    from: isA2AMessage ? `Agent (${metadata.fromHubAddress})` : isRoomMessage ? (senderDisplayName ?? "Room Member") : "Owner",
    timestamp: new Date(metadata.timestamp).getTime(),
    previousTimestamp,
    envelope: envelopeOptions,
    body: contextBody,
  });

  // Build attachment fields for the context payload
  const attachmentFields: Record<string, any> = {};
  if (metadata.attachment) {
    attachmentFields.AttachmentPath = metadata.attachment.filePath;
    attachmentFields.AttachmentFilename = metadata.attachment.filename;
    attachmentFields.AttachmentMime = metadata.attachment.mime;

    // For images: include as MediaUrl so the LLM can see the visual content
    if (metadata.attachment.base64) {
      attachmentFields.MediaUrl = metadata.attachment.base64;
      attachmentFields.NumMedia = "1";
    }

    // For text files: content is already inlined in plaintext body
  }

  // Inject renter credentials for room messages (marketplace rentals)
  const credentialFields: Record<string, any> = {};
  if (isRoomMessage && metadata.roomId && channel.getCredentials) {
    const creds = channel.getCredentials(metadata.roomId);
    if (creds.length > 0) {
      credentialFields.HasRenterCredentials = true;
      credentialFields.RenterCredentials = channel.getCredentialMap(metadata.roomId);
      credentialFields.RenterCredentialCount = creds.length;
    }
  }

  const ctxPayload = core.channel.reply.finalizeInboundContext({
    Body: body,
    RawBody: plaintext,
    CommandBody: plaintext,
    From: isA2AMessage ? `a2a:${metadata.fromHubAddress}` : isRoomMessage ? `agentvault:room:${metadata.roomId}` : "agentvault:owner",
    // GH #366 iter 3: room `To` must be ROOM-scoped (matches `From`), not
    // agent-scoped — an agent-scoped `To` reads as a 1:1 address and triggers
    // the `chat_type: "direct"` confabulation. Only the ROOM case changes;
    // direct and A2A keep the agent-scoped `To` they always had. Reply routing
    // is unaffected (it uses an explicit room replyTarget, not `To`).
    To: isRoomMessage
      ? _buildRoomTo(metadata.roomId, account.accountId)
      : `agentvault:agent:${account.accountId}`,
    SessionKey: route.sessionKey,
    AccountId: account.accountId,
    ChatType: isA2AMessage ? "a2a" : isRoomMessage ? "room" : "direct",
    ConversationLabel: isA2AMessage ? `A2A: ${metadata.fromHubAddress}` : isRoomMessage ? (roomName ?? "AgentVault Room") : "AgentVault",
    SenderName: isA2AMessage ? metadata.fromHubAddress : isRoomMessage ? (senderDisplayName ?? "Room Member") : "Owner",
    // GH #366: room SenderId must encode the ROOM, not the sender's device id.
    // The old form `agentvault:room:${senderDeviceId}` stamped the device id
    // with a `room:` prefix → agent read it as a per-device DM channel.
    // Fragment form `agentvault:room:<roomId>#device:<deviceId>` keeps the room
    // scope primary while still identifying the specific sender within it. The
    // no-senderDeviceId case is hardened in _buildRoomSenderId (sender-name
    // fragment) so the binding exact-match can't pass for every member.
    SenderId: isA2AMessage
      ? `a2a:${metadata.fromHubAddress}`
      : isRoomMessage
      ? _buildRoomSenderId(metadata.roomId, metadata.senderDeviceId, senderDisplayName)
      : "agentvault:owner",
    Provider: "agentvault",
    Surface: "agentvault",
    MessageSid: metadata.messageId,
    Timestamp: new Date(metadata.timestamp).getTime(),
    OriginatingChannel: "agentvault",
    // GH #366 iter 3: keep OriginatingTo consistent with `To` for room messages
    // so the agent can't find an agent-scoped address in either field and read
    // it as a 1:1.
    OriginatingTo: isRoomMessage
      ? _buildRoomTo(metadata.roomId, account.accountId)
      : `agentvault:agent:${account.accountId}`,
    CommandAuthorized: true,
    Instrument: _instrument,           // OpenClaw ctx convention
    AgentVaultTelemetry: _instrument,  // AgentVault-specific accessor
    ...attachmentFields,
    ...credentialFields,
  });

  // Serialize the reply-session init + entire turn per session key so a burst of
  // inbounds for the same room/DM can't race OpenClaw's optimistic session-init
  // commit (see _withSessionSerial above). Turns for different sessions still run
  // concurrently; only same-session turns queue and run one at a time in order.
  // route.sessionKey is the typed (string) source the core also conflicts on; use it
  // directly. Guard a contract slip (non-string/empty) from collapsing unrelated
  // sessions onto one "undefined" key — fall back to a per-turn-unique key so such a
  // turn runs without falsely serializing against others (dr-evil M1, 2026-07-08).
  const _turnSessionKey =
    typeof route.sessionKey === "string" && route.sessionKey
      ? route.sessionKey
      : `__unkeyed__:${traceId}`;
  // #1418: an owner 1:1 turn runs as THAT conversation's turn, so anything
  // the agent sends during it without naming a conversation — the `message`
  // tool, a skill's /send or /decision, an artifact — reaches the person who
  // asked and nobody else in the account. Inside the serial queue, so a turn
  // still waiting behind another one is not yet "in progress". The group the
  // message decrypted under goes with it: it places the person even when the
  // local conversation map is stale or does not know the conversation yet.
  const _asTurn = <T>(fn: () => Promise<T>): Promise<T> =>
    !isRoomMessage && !isA2AMessage && typeof channel.runForConversation === "function"
      ? channel.runForConversation(metadata.conversationId, fn, { groupId: metadata.conversationGroupId })
      : fn();
  await _withSessionSerial(_turnSessionKey, () => _asTurn(() =>
    _withTurnTimeout(TURN_BUDGET_MS, async () => {
  await core.channel.session.recordInboundSession({
    storePath,
    sessionKey: ctxPayload.SessionKey ?? route.sessionKey,
    ctx: ctxPayload,
    onRecordError: (err: Error) => {
      core.error?.(`[AgentVault] session record failed: ${String(err)}`);
    },
  });

  try {
    await runWithTraceContext(
      { traceId, parentSpanId: inferenceSpanId },
      () => core.channel.reply.dispatchReplyWithBufferedBlockDispatcher({
      ctx: ctxPayload,
      cfg,
      dispatcherOptions: {
        deliver: async (payload: { text?: string; kind?: string }) => {
          // Filter out thinking/reasoning blocks — only deliver actual responses
          if (payload.kind === "thinking" || payload.kind === "reasoning") return;
          const text = (payload.text ?? "").trim();
          if (!text) return;
          // Stash a think-stripped candidate BEFORE the reasoning filter below, so
          // if every block ends up filtered we can still recover a real answer
          // (regression 2026-07-08). Gate it with the SAME leak heuristic used to
          // filter, so a stashed fallback can never be raw chain-of-thought or a
          // directive (code-review 2026-07-08). Consulted only when replyCount===0.
          {
            const recoverable = _stripThinkBlocks(text);
            if (recoverable && !_looksLikeReasoningLeak(recoverable)) lastFilteredText = recoverable;
          }
          // Skip blocks that look like leaked chain-of-thought / <think> XML / bare
          // "think" / OpenClaw directive tags — same test as the stash gate above.
          if (_looksLikeReasoningLeak(text)) return;

          const replyStart = Date.now();
          replyCount++;
          totalReplyChars += text.length;
          if (!firstReplyTime) firstReplyTime = replyStart;

          // Route reply via deliver() — explicit target, no silent fallback
          let replyTarget: DeliveryTarget;
          if (isA2AMessage) {
            // Loop prevention: rate limit A2A replies per channel
            if (!_a2aCanReply(metadata.a2aChannelId)) {
              console.warn(`[AgentVault] A2A reply suppressed (rate limit) for channel ${metadata.a2aChannelId?.slice(0, 8)}...`);
              return;
            }
            replyTarget = { kind: "a2a", hubAddress: metadata.fromHubAddress };
          } else if (isRoomMessage) {
            // Loop prevention: a safety net beyond the @ protocol. Bypassed
            // for human-prompted replies — see _roomReplyAllowed.
            if (!_roomReplyAllowed(metadata.senderIsAgent, metadata.roomId)) {
              console.warn(`[AgentVault] Room reply suppressed (rate limit) for room ${metadata.roomId?.slice(0, 8)}...`);
              return;
            }
            replyTarget = { kind: "room", roomId: metadata.roomId };
          } else {
            replyTarget = { kind: "owner" };
          }
          await channel.deliver(replyTarget, { type: "text", text }, {
            conversationId: metadata.conversationId,
            topicId: metadata.topicId,
            parentSpanId: inferenceSpanId,
            metadata: isRoomMessage ? { content_length: text.length } : undefined,
          });
          if (isA2AMessage) {
            _a2aRecordReply(metadata.a2aChannelId);
          }
          if (isRoomMessage) {
            _roomRecordReply(metadata.roomId);
          }

          const replyEnd = Date.now();
          replySpans.push({ startTime: replyStart, endTime: replyEnd, chars: text.length, index: replyCount });
        },
        onError: (err: Error, info?: { kind?: string }) => {
          core.error?.(`[AgentVault] ${info?.kind ?? "reply"} error: ${String(err)}`);
        },
      },
      replyOptions: {},
    }),
    );

    // Recovery (regression 2026-07-08): a reasoning-heavy model (deepseek) can emit
    // a turn whose blocks are ALL filtered as thinking/<think> above → replyCount
    // stays 0 and the room/owner got silence with NO [deliver] and NO error — a
    // silent drop that looks identical to "the agent chose not to answer". If we
    // captured any real (think-stripped) content, deliver it ONCE rather than drop
    // the whole turn. Rate-limited like any reply; observable via a warn.
    if (replyCount === 0 && lastFilteredText) {
      const canReply = isA2AMessage
        ? _a2aCanReply(metadata.a2aChannelId)
        : isRoomMessage
        // A2A is agent-to-agent by definition, so it is never bypassed — that
        // is precisely the case the guard exists for.
        ? _roomReplyAllowed(metadata.senderIsAgent, metadata.roomId)
        : true;
      if (canReply) {
        const replyTarget: DeliveryTarget = isA2AMessage
          ? { kind: "a2a", hubAddress: metadata.fromHubAddress }
          : isRoomMessage
          ? { kind: "room", roomId: metadata.roomId }
          : { kind: "owner" };
        console.warn(
          `[AgentVault] all reply blocks filtered as reasoning — delivering recovered fallback (${lastFilteredText.length} chars)`,
        );
        await channel.deliver(replyTarget, { type: "text", text: lastFilteredText }, {
          conversationId: metadata.conversationId,
          topicId: metadata.topicId,
          parentSpanId: inferenceSpanId,
          metadata: isRoomMessage ? { content_length: lastFilteredText.length } : undefined,
        });
        replyCount++;
        totalReplyChars += lastFilteredText.length;
        if (!firstReplyTime) firstReplyTime = Date.now();
        if (isA2AMessage) _a2aRecordReply(metadata.a2aChannelId);
        else if (isRoomMessage) _roomRecordReply(metadata.roomId);
      } else {
        console.warn("[AgentVault] reply blocks all filtered AND rate-limited — no fallback delivered");
      }
    } else if (replyCount === 0) {
      // No recoverable text at all: don't post noise, but make the silence visible.
      console.warn("[AgentVault] turn produced no deliverable reply (all blocks empty/filtered, nothing recoverable)");
    }

    const endTime = Date.now();

    // Emit child spans for the completed message lifecycle
    _emitHierarchicalSpans(channel, {
      traceId,
      rootSpanId,
      inferenceSpanId,
      startTime,
      endTime,
      firstReplyTime,
      replySpans,
      inputChars: plaintext.length,
      replyCount,
      totalReplyChars,
      isRoom: isRoomMessage,
      status: "ok",
    });
  } catch (err) {
    const endTime = Date.now();

    // Emit spans even on error — shows where failure occurred
    _emitHierarchicalSpans(channel, {
      traceId,
      rootSpanId,
      inferenceSpanId,
      startTime,
      endTime,
      firstReplyTime,
      replySpans,
      inputChars: plaintext.length,
      replyCount,
      totalReplyChars,
      isRoom: isRoomMessage,
      status: "error",
      statusMessage: String(err),
    });
    // Dedicated error span for the Errors tab
    _emitChildSpan(channel, {
      traceId,
      parentSpanId: rootSpanId,
      name: "error",
      startTime: endTime,
      endTime: endTime,
      attributes: {
        "ai.agent.error.type": (err as any)?.constructor?.name ?? "Error",
        "ai.agent.error.message": String(err),
      },
      status: "error",
      statusMessage: String(err),
    });
    throw err;
  }
  }))); // end _withTurnTimeout + _asTurn + _withSessionSerial
}

/** Infer a span type from the span name for activity stream display. */
function _inferSpanType(name: string): "llm" | "tool" | "http" | "action" | "error" | "nav" {
  if (name === "error" || name.includes("error")) return "error";
  if (name.startsWith("llm.") || name.includes("inference")) return "llm";
  if (name.startsWith("tool.") || name.includes("tool")) return "tool";
  if (name.startsWith("http.") || name.includes("http")) return "http";
  if (name.startsWith("nav.") || name.includes("nav")) return "nav";
  if (name.startsWith("action.") || name.includes("action")) return "action";
  return "action"; // default fallback
}

/** Extract a human-readable span name from span attributes. */
function _extractSpanName(name: string, attrs: Record<string, string | number | boolean>): string {
  if (attrs["ai.agent.llm.model"]) return String(attrs["ai.agent.llm.model"]);
  if (attrs["ai.agent.tool.name"]) return String(attrs["ai.agent.tool.name"]);
  if (attrs["ai.agent.http.url"]) {
    try { return new URL(String(attrs["ai.agent.http.url"])).hostname; } catch { /* use raw */ }
    return String(attrs["ai.agent.http.url"]).slice(0, 40);
  }
  if (attrs["ai.agent.action.type"]) return `${attrs["ai.agent.action.type"]}: ${attrs["ai.agent.action.target"] ?? ""}`;
  if (attrs["ai.agent.nav.to_url"]) return String(attrs["ai.agent.nav.to_url"]).slice(0, 40);
  if (attrs["ai.agent.error.type"]) return String(attrs["ai.agent.error.type"]);
  return name;
}

/** Emit a single child span via the channel's telemetry reporter + WS activity stream. */
function _emitChildSpan(channel: any, opts: {
  traceId: string;
  parentSpanId: string;
  spanId?: string;
  name: string;
  startTime: number;
  endTime: number;
  attributes: Record<string, string | number | boolean>;
  status: "ok" | "error";
  statusMessage?: string;
}): void {
  try {
    const reporter = channel.telemetry;
    if (!reporter) return;

    const spanId = opts.spanId ?? randomBytes(8).toString("hex");

    reporter.reportCustomSpan({
      traceId: opts.traceId,
      spanId,
      parentSpanId: opts.parentSpanId,
      name: opts.name,
      kind: "internal" as const,
      startTime: opts.startTime,
      endTime: opts.endTime,
      attributes: opts.attributes,
      status: {
        code: opts.status === "ok" ? 1 : 2,
        message: opts.statusMessage,
      },
    });

    // Dual-path: also send via WS for real-time activity streaming
    try {
      const spanType = _inferSpanType(opts.name);
      const spanName = _extractSpanName(opts.name, opts.attributes);
      const durationMs = opts.endTime - opts.startTime;
      channel.sendActivitySpan({
        trace_id: opts.traceId,
        span_id: spanId,
        parent_span_id: opts.parentSpanId,
        span_type: spanType,
        span_name: spanName,
        status: opts.status,
        start_time: new Date(opts.startTime).toISOString(),
        end_time: new Date(opts.endTime).toISOString(),
        duration_ms: durationMs,
        attributes: opts.attributes,
        ...(spanType === "llm" ? {
          tokens_input: opts.attributes["ai.agent.llm.tokens_input"],
          tokens_output: opts.attributes["ai.agent.llm.tokens_output"],
        } : {}),
        ...(spanType === "http" ? {
          http_method: opts.attributes["ai.agent.http.method"],
          http_status_code: opts.attributes["ai.agent.http.status_code"],
          http_url: opts.attributes["ai.agent.http.url"],
        } : {}),
        ...(spanType === "tool" ? {
          tool_success: opts.attributes["ai.agent.tool.success"],
        } : {}),
        ...(spanType === "error" ? {
          error_type: opts.attributes["ai.agent.error.type"],
          error_message: opts.attributes["ai.agent.error.message"],
        } : {}),
      });
    } catch { /* WS activity is best-effort */ }
  } catch { /* telemetry is best-effort */ }
}

/** Emit the full hierarchical span tree for a message lifecycle. */
function _emitHierarchicalSpans(channel: any, opts: {
  traceId: string;
  rootSpanId: string;
  inferenceSpanId?: string;
  startTime: number;
  endTime: number;
  firstReplyTime: number | null;
  replySpans: Array<{ startTime: number; endTime: number; chars: number; index: number }>;
  inputChars: number;
  replyCount: number;
  totalReplyChars: number;
  isRoom: boolean;
  status: "ok" | "error";
  statusMessage?: string;
}): void {
  try {
    const reporter = channel.telemetry;
    if (!reporter) return;

    // 1. Inference span: from message arrival to first reply (or end if no replies)
    const inferenceEnd = opts.firstReplyTime ?? opts.endTime;
    _emitChildSpan(channel, {
      traceId: opts.traceId,
      parentSpanId: opts.rootSpanId,
      spanId: opts.inferenceSpanId,
      name: "ai.agent.inference",
      startTime: opts.startTime + 1, // just after receive
      endTime: inferenceEnd,
      attributes: {
        "ai.agent.inference.latency_ms": inferenceEnd - opts.startTime,
        "ai.agent.message.input_chars": opts.inputChars,
      },
      status: opts.status,
      statusMessage: opts.status === "error" ? opts.statusMessage : undefined,
    });

    // 2. Individual reply spans
    for (const reply of opts.replySpans) {
      _emitChildSpan(channel, {
        traceId: opts.traceId,
        parentSpanId: opts.rootSpanId,
        name: "ai.agent.reply",
        startTime: reply.startTime,
        endTime: reply.endTime,
        attributes: {
          "ai.agent.reply.index": reply.index,
          "ai.agent.reply.chars": reply.chars,
        },
        status: "ok",
      });
    }

    // 3. Root span (wraps everything) — emitted last.
    // ai.agent.task.status is what Phantom Victory detection queries
    // (observe_service.py:_SUCCESS_VALUES = "success" | "ok" | "completed" | "finished").
    reporter.reportCustomSpan({
      traceId: opts.traceId,
      spanId: opts.rootSpanId,
      name: "ai.agent.message",
      kind: "server" as const,
      startTime: opts.startTime,
      endTime: opts.endTime,
      attributes: {
        "ai.agent.message.input_chars": opts.inputChars,
        "ai.agent.message.reply_count": opts.replyCount,
        "ai.agent.message.reply_chars": opts.totalReplyChars,
        "ai.agent.message.latency_ms": opts.endTime - opts.startTime,
        "ai.agent.message.type": opts.isRoom ? "room" : "direct",
        "ai.agent.task.status": opts.status === "ok" ? "success" : "failure",
      },
      status: {
        code: opts.status === "ok" ? 1 : 2,
        message: opts.statusMessage,
      },
    });
  } catch { /* telemetry is best-effort */ }
}

// --- Channel plugin definition ---

const agentVaultPlugin = {
  id: "agentvault",
  meta: {
    id: "agentvault",
    label: "AgentVault",
    selectionLabel: "AgentVault (E2E Encrypted)",
    docsPath: "https://agentvault.chat/docs",
    blurb: "End-to-end encrypted messaging between owners and their AI agents.",
    aliases: ["av", "agent-vault"],
  },
  capabilities: { chatTypes: ["direct", "room"] },
  config: { listAccountIds, resolveAccount },

  // Tells OC core's resolveMessagingTarget that any plausible AgentVault target
  // string is an ID, short-circuiting the directory lookup that would otherwise
  // throw "Unknown target". Without this hook, OC tries to find e.g.
  // "a2a:smartcode.agentvault.hub" in a user/group directory, finds nothing,
  // and rejects the tool call before deliver is ever called.
  messaging: {
    normalizeTarget: (target: string): string => _normalizeOutboundTo(target),
    targetResolver: {
      looksLikeId: (target: string): boolean => {
        const t = target.trim();
        if (!t) return false;
        if (t === "owner" || t === "default" || t === "context") return true;
        if (t.startsWith("a2a:") || t.startsWith("room:") || t.startsWith("agent:")) return true;
        if (t.startsWith("agentvault:") || t.startsWith("a2a_channel:") || t.startsWith("channel:")) return true;
        // Match registered targets (including aliases like bare shortname, hub address).
        return _outboundTargets.some((tt) => tt.id === t);
      },
      hint: 'Use "owner" for the owner DM or "a2a:<hubAddress>" (e.g. a2a:smartcode.agentvault.hub) for A2A peers; "smartcode" or "smartcode.agentvault.hub" also work as aliases.',
    },
  },

  gateway: {
    /** Health probe for `openclaw channels status --probe` */
    probe: async (ctx: any) => {
      const accountId = ctx?.accountId ?? "default";
      const ch = _channels.get(accountId);
      if (!ch) return { ok: false, status: "disconnected", error: "Channel not started" };
      const state = ch.state;
      return {
        ok: state === "ready",
        status: state,
        deviceId: ch.deviceId ?? undefined,
        sessions: ch.sessionCount,
      };
    },

    /** Status for `openclaw health --json` per-channel summary */
    status: (ctx: any) => {
      const accountId = ctx?.accountId ?? "default";
      const ch = _channels.get(accountId);
      if (!ch) return { connected: false, status: "not_started" };
      return {
        connected: ch.state === "ready",
        status: ch.state,
        deviceId: ch.deviceId ?? undefined,
        sessions: ch.sessionCount,
        encrypted: true,
      };
    },

    startAccount: async (ctx: ChannelGatewayContext<ResolvedAccountBase>) => {
      const { account, cfg, log, abortSignal } = ctx;
      const _log: ((msg: string) => void) | undefined = typeof log === "function" ? log : log?.info?.bind(log);

      if (!account.configured) {
        throw new Error(
          "AgentVault channel not configured. Run: npx @agentvault/agentvault setup --token=av_tok_...\nThen restart OpenClaw.",
        );
      }

      const dataDir = pathResolve(account.dataDir.replace(/^~/, osHomedir()));
      _log?.(`[AgentVault] starting (dataDir=${dataDir})`);

      // startAccount must STAY PENDING while the channel is running.
      // Resolving signals "channel stopped" to the gateway health monitor,
      // which triggers auto-restart. We block here until the abortSignal
      // fires (gateway shutdown / config reload), then clean up.
      await new Promise<void>((resolve, reject) => {
        let channel: any;
        // #395: set when a TERMINAL auth failure (device revoked/replaced) is
        // handled. Suppresses the onStateChange "error" → reject below, which
        // the host treats as "restart this account" — restarting into dead
        // creds is exactly the crash-loop we're fixing.
        let terminalStopped = false;

        const onAbort = async () => {
          await channel?.stop();
          _channels.delete(account.accountId);
          resolve();
        };

        abortSignal?.addEventListener("abort", () => void onAbort());

        // Lazy import — defers libsodium initialization until channel starts
        import("./index.js").then(async ({ SecureChannel }) => {
          channel = new SecureChannel({
            inviteToken: "",
            dataDir,
            apiUrl: account.apiUrl,
            agentName: account.agentName,
            onMessage: _makeOnMessage({
              inbound: ({ plaintext, metadata }) =>
                handleInbound({ plaintext, metadata, channel, account, cfg }),
              getChannel: () => channel,
              isRuntimeReady: () => !!_ocRuntime,
              enqueue: (fn) => { _messageQueue.push(fn); },
              log: (m) => _log?.(m),
            }),
            onA2AMessage: async (msg: any) => {
              const a2aMetadata = {
                a2aChannelId: msg.channelId,
                fromHubAddress: msg.fromHubAddress,
                conversationId: msg.conversationId,
                timestamp: msg.timestamp,
                parentSpanId: msg.parentSpanId,
                messageId: `a2a:${msg.channelId}:${Date.now()}`,
              };
              if (!_ocRuntime) {
                _log?.("[AgentVault] runtime not ready, queuing A2A message");
                _messageQueue.push(async () => {
                  await handleInbound({ plaintext: msg.text, metadata: a2aMetadata, channel, account, cfg });
                });
                return;
              }
              try {
                await handleInbound({ plaintext: msg.text, metadata: a2aMetadata, channel, account, cfg });
              } catch (err) {
                _log?.(`[AgentVault] A2A inbound error: ${String(err)}`);
              }
            },
            onA2AChannelReady: async (info: {
              channelId: string;
              peerHubAddress: string;
              role: "creator" | "member";
              conversationId: string;
              topic?: string;
            }) => {
              // Register peer as valid outbound target (both roles)
              _registerA2ATarget(info.peerHubAddress, account.accountId);

              // Only creator sends the seed message
              if (info.role !== "creator") {
                _log?.(`[AgentVault] A2A channel ready (member) — waiting for creator: ${info.peerHubAddress}`);
                return;
              }
              _log?.(`[AgentVault] A2A channel ready (creator) — sending seed to ${info.peerHubAddress}${info.topic ? ` [topic: ${info.topic}]` : ""}`);

              const seedText = info.topic
                ? `[System] A new A2A channel has been established with agent ${info.peerHubAddress}. ` +
                  `Topic: "${info.topic}". ` +
                  `Introduce yourself briefly and begin discussing this topic.`
                : `[System] A new A2A channel has been established with agent ${info.peerHubAddress}. ` +
                  `Introduce yourself briefly and state what you can help with.`;
              const seedMetadata = {
                a2aChannelId: info.channelId,
                fromHubAddress: info.peerHubAddress,
                conversationId: info.conversationId,
                timestamp: new Date().toISOString(),
                messageId: `a2a-seed:${info.channelId}:${Date.now()}`,
              };

              if (!_ocRuntime) {
                _log?.("[AgentVault] runtime not ready, queuing A2A seed");
                _messageQueue.push(async () => {
                  await handleInbound({ plaintext: seedText, metadata: seedMetadata, channel, account, cfg });
                });
                return;
              }
              try {
                await handleInbound({ plaintext: seedText, metadata: seedMetadata, channel, account, cfg });
              } catch (err) {
                _log?.(`[AgentVault] A2A seed error: ${String(err)}`);
              }
            },
            onStateChange: (state: string) => {
              _log?.(`[AgentVault] → ${state}`);
              // "error" is a permanent failure — reject so the host can restart.
              // #395: defer one microtask and guard on terminalStopped. A
              // terminal event (auth_failed / session_replaced / "Device was
              // revoked" / reconnect-loop) is emitted in the SAME tick right
              // after _setState("error"); attachLifecycle's onTerminal sets the
              // flag synchronously, pre-empting the restart so a revoked device
              // goes idle instead of crash-looping. Only a NON-terminal "error"
              // (e.g. a transient connect failure) survives to reject→restart.
              if (state === "error") {
                queueMicrotask(() => {
                  if (!terminalStopped) reject(new Error("AgentVault channel permanent error"));
                });
              }
              // All other states (connecting/ready/disconnected) are handled
              // internally by SecureChannel's reconnect logic — do NOT resolve.
            },
          });

          _channels.set(account.accountId, channel);

          // Prevent unhandled "error" events from crashing the process.
          // Without this handler, Node.js EventEmitter throws on emit("error").
          channel.on("error", (err: any) => {
            // #718: "non-fatal" was true of the PROCESS and false of the
            // MESSAGE. A server rejection reaching here means a frame already
            // logged as `result=sent` was refused and dropped; filing that
            // under the same wording as a transient blip is how 707 of them
            // went unread between 2026-07-02 and 2026-07-31.
            _log?.(formatChannelError(err));
          });

          // #395: on a TERMINAL auth failure (device revoked/replaced, session
          // taken over) stop THIS agent's channel and go idle with a re-enroll
          // message — instead of crash-looping into the server's denylist. The
          // process + any other agents stay up. Transient errors keep riding
          // the channel's built-in reconnect (the listener above survives them).
          attachLifecycle(channel, {
            agentName: account.agentName,
            log: (m: string) => _log?.(`[AgentVault] ${m}`),
            onTerminal: async () => {
              terminalStopped = true; // synchronous: pre-empts the deferred reject→restart
              try {
                await channel.stop();
              } catch {
                /* stop is best-effort */
              }
              // Drop the now-dead channel from the routing map so outbound
              // sends/broadcasts don't silently no-op against a closed WS.
              _channels.delete(account.accountId);
              // Intentionally leave the account promise PENDING (idle): settling
              // it would signal the host to auto-restart into the dead creds.
              // abortSignal still tears down cleanly on gateway shutdown/reload.
            },
          });

          // --- Activate MCP server with agent skills ---
          try {
            const { AgentVaultMcpServer } = await import("./mcp-server.js");
            const { loadSkillsFromDirectory, loadSkillsFromApi, mergeSkills } = await import("./skill-manifest.js");
            const mcpServer = new AgentVaultMcpServer({
              agentName: account.agentName ?? "agent",
              apiUrl: account.apiUrl ?? "https://api.agentvault.chat",
              onInvoke: async (skillName: string, args: Record<string, unknown>, skill: any) => {
                // Execute skill via backend inline executor (owner path — device JWT auth)
                // Sends skill instructions + input to Claude API through the backend
                const apiUrl = account.apiUrl ?? "https://api.agentvault.chat";
                const jwt = (channel as any)._deviceJwt;
                if (!jwt) {
                  return { error: "Not connected — device JWT unavailable" };
                }

                // Build the execution request with instructions from the local skill definition
                const instructions = skill?.instructions;
                if (!instructions) {
                  return { error: `Skill "${skillName}" has no instructions defined` };
                }

                try {
                  const resp = await fetch(`${apiUrl}/api/v1/capabilities/owner-execute`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${jwt}`,
                    },
                    body: JSON.stringify({
                      jsonrpc: "2.0",
                      method: "tools/call",
                      params: {
                        name: skillName,
                        arguments: args,
                        // Pass instructions from local SKILL.md so backend doesn't need DB lookup
                        _instructions: instructions,
                        _schema: skill?.schema,
                      },
                      id: `invoke-${Date.now()}`,
                    }),
                  });
                  if (!resp.ok) {
                    const detail = await resp.text();
                    return { error: `Execution failed (${resp.status}): ${detail}` };
                  }
                  const rpcResult = await resp.json();
                  if (rpcResult.error) {
                    return { error: rpcResult.error.message ?? "Execution error" };
                  }
                  // Extract text content from MCP response
                  const content = rpcResult.result?.content;
                  if (Array.isArray(content) && content.length > 0) {
                    return content[0].text ?? JSON.stringify(content);
                  }
                  return rpcResult.result ?? { executed: true };
                } catch (err) {
                  _log?.(`[AgentVault] Skill execution failed: ${String(err)}`);
                  return { error: `Skill execution failed: ${String(err)}` };
                }
              },
            });

            // Resolve the OpenClaw workspace directory for this agent.
            // cfg is the full openclaw.json — agents.list[] has per-agent workspace,
            // agents.defaults.workspace is the fallback for the main agent.
            const homedir = osHomedir();
            const ocCfg = cfg as any;
            let workspaceDir = dataDir; // fallback to dataDir if workspace unknown
            try {
              const agentsList: any[] = ocCfg?.agents?.list ?? [];
              // Match agent entry by accountId — bindings map accountId→agentId,
              // and agent entries have workspace paths
              const bindings: any[] = ocCfg?.bindings ?? [];
              const binding = bindings.find((b: any) =>
                b.match?.channel === "agentvault" && b.match?.accountId === account.accountId,
              );
              const agentId = binding?.agentId ?? "main";
              const agentEntry = agentsList.find((a: any) => a.id === agentId);

              if (agentEntry?.workspace) {
                workspaceDir = pathResolve(agentEntry.workspace.replace(/^~/, homedir));
              } else if (ocCfg?.agents?.defaults?.workspace) {
                workspaceDir = pathResolve(ocCfg.agents.defaults.workspace.replace(/^~/, homedir));
              }
              _log?.(`[AgentVault] MCP skill scan: workspace=${workspaceDir} (agentId=${agentId})`);
            } catch {
              _log?.(`[AgentVault] MCP skill scan: falling back to dataDir`);
            }

            // Load skills from workspace SKILL.md files
            const localSkills = loadSkillsFromDirectory(workspaceDir);

            // Also try loading from a dedicated skills/ subdirectory
            const skillsSubDir = pathResolve(workspaceDir, "skills");
            const subDirSkills = loadSkillsFromDirectory(skillsSubDir);
            localSkills.push(...subDirSkills);

            // Also scan the dataDir in case skills are stored there
            if (workspaceDir !== dataDir) {
              const dataDirSkills = loadSkillsFromDirectory(dataDir);
              localSkills.push(...dataDirSkills);
            }

            // Load skills published via Skill Builder (from backend API)
            let apiSkills: import("./mcp-server.js").SkillDefinition[] = [];
            try {
              const { loadState } = await import("./state.js");
              const persisted = await loadState(dataDir);
              const hubId = persisted?.hubId;
              const deviceJwt = persisted?.deviceJwt;
              const apiUrl = account.apiUrl ?? "https://api.agentvault.chat";
              if (hubId && deviceJwt) {
                apiSkills = await loadSkillsFromApi(apiUrl, deviceJwt, hubId);
                if (apiSkills.length > 0) {
                  _log?.(`[AgentVault] Loaded ${apiSkills.length} skill(s) from API`);
                }
              }
            } catch (err) {
              _log?.(`[AgentVault] API skill loading failed (non-fatal): ${String(err)}`);
            }

            // Merge local + API skills (local takes precedence)
            const { skills: allSkills } = mergeSkills(localSkills, apiSkills);
            for (const skill of allSkills) {
              mcpServer.registerSkill(skill);
            }

            mcpServer.initialize();
            channel.setMcpServer(mcpServer);
            _log?.(`[AgentVault] MCP server activated with ${mcpServer.skillCount} skill(s) (${localSkills.length} local, ${apiSkills.length} API)`);
          } catch (err) {
            _log?.(`[AgentVault] MCP server activation failed (non-fatal): ${String(err)}`);
          }

          // Always start local HTTP server — managed routes are an overlay, not a replacement
          const httpPort = account.httpPort ?? 18790;
          channel.on("ready", () => {
            channel.startHttpServer(httpPort);
            _log?.(`[AgentVault] HTTP send server listening on http://127.0.0.1:${httpPort}`);
            if (managedRoutesRequested) {
              // #1042 — say what we DID, not what we cannot observe: a rejected
              // registration surfaces only in the gateway's own diagnostics.
              _log?.(`[AgentVault] OpenClaw managed routes requested under /agentvault/* (auth=gateway); the gateway reports any rejection in its [plugins] diagnostics — check \`openclaw plugins doctor\``);
            }

            // Register persisted A2A peers as valid outbound targets
            for (const peerAddress of channel.a2aPeerAddresses) {
              _registerA2ATarget(peerAddress, account.accountId);
            }

            // Register persisted rooms as valid outbound targets
            for (const room of channel.roomIds) {
              _registerRoomTarget(room.roomId, room.name, account.accountId);
            }
          });

          // Re-register A2A targets after server sync (fixes stale persisted addresses)
          channel.on("a2a_channels_synced", () => {
            for (const peerAddress of channel.a2aPeerAddresses) {
              _registerA2ATarget(peerAddress, account.accountId);
            }
          });

          // Register new rooms as outbound targets when agent joins
          channel.on("room_joined", (info: { roomId: string; name: string }) => {
            _registerRoomTarget(info.roomId, info.name, account.accountId);
          });

          // Guard the startup-reject with terminalStopped too: a terminal
          // surfaced as a start() rejection (e.g. enrollment-time revoked)
          // must not reject→restart into dead creds.
          channel.start().catch((e: unknown) => {
            if (!terminalStopped) reject(e);
          });
        }).catch(reject);
      });

      return { stop: async () => {} }; // Channel already stopped via abortSignal by this point
    },
  },

  outbound: {
    deliveryMode: "direct" as const,

    // Register valid send targets so OpenClaw's `message` tool can route
    // proactive (agent-initiated) sends — not just replies to inbound messages.
    targets: _outboundTargets,

    sendText: async ({ to: rawTo, text, accountId }: { to: string; text: string; accountId?: string }) => {
      const resolvedId = accountId ?? "default";
      const ch = _channels.get(resolvedId);
      if (!ch) return { ok: false, error: "AgentVault channel not connected" };
      try {
        const target = parseTarget(_normalizeOutboundTo(rawTo));
        const receipt = await ch.deliver(target, { type: "text", text });
        return { ok: receipt.ok, queued: receipt.queued, error: receipt.error };
      } catch (err) {
        return { ok: false, error: String(err) };
      }
    },

    sendMedia: async ({ to: rawTo, text, mediaUrl, accountId }: { to: string; text?: string; mediaUrl: string; accountId?: string }) => {
      const resolvedId = accountId ?? "default";
      const ch = _channels.get(resolvedId);
      if (!ch) return { ok: false, error: "AgentVault channel not connected" };
      try {
        // For now, send media URL as text — AgentVault handles attachments separately
        const message = text ? `${text}\n${mediaUrl}` : mediaUrl;
        const target = parseTarget(_normalizeOutboundTo(rawTo));
        const receipt = await ch.deliver(target, { type: "text", text: message });
        return { ok: receipt.ok, queued: receipt.queued, error: receipt.error };
      } catch (err) {
        return { ok: false, error: String(err) };
      }
    },

    /** Rich payload delivery — OpenClaw v2026.3.2+ calls this for structured messages. */
    sendPayload: async (ctx: ChannelOutboundPayloadContext) => {
      const { payload, accountId } = ctx;

      // Suppress reasoning blocks — E2E channel should only deliver actual responses
      if (payload.isReasoning) return { ok: true };

      // Short-circuit empty content before checking channel — nothing to send
      const text = (payload.text ?? "").trim();
      if (!text && !payload.mediaUrls?.length) return { ok: true };

      const resolvedId = accountId ?? "default";
      const ch = _channels.get(resolvedId);
      if (!ch) return { ok: false, error: "AgentVault channel not connected" };

      try {
        // Wake agent proactively before sending (fire-and-forget)
        requestHeartbeatNow({ reason: "outbound-payload" }).catch(() => {});

        // Resolve target via parseTarget — owner if no explicit target
        const rawTarget = (ctx as any).to;
        let target: DeliveryTarget;
        if (rawTarget && typeof rawTarget === "string") {
          target = parseTarget(rawTarget);
        } else {
          target = { kind: "owner" };
        }

        // Helper: deliver text via unified deliver()
        const _send = async (msg: string) => {
          await ch.deliver(target, { type: "text", text: msg });
        };

        // Encrypt and deliver text content
        if (text) {
          await _send(text);
        }

        // Deliver media URLs as text messages (E2E encrypted)
        if (payload.mediaUrls?.length) {
          for (const url of payload.mediaUrls) {
            await _send(url);
          }
        }

        // Forward suggested actions as a structured message
        if (payload.suggestedActions?.length) {
          const actionsText = payload.suggestedActions
            .map((a: { label: string; action: string }) => `- ${a.label}: ${a.action}`)
            .join("\n");
          await _send(`Suggested actions:\n${actionsText}`);
        }

        return { ok: true };
      } catch (err) {
        return { ok: false, error: String(err) };
      }
    },
  },
};

// --- Exported for testing ---
export { handleInbound as _handleInbound, _parseMentions, _owesRoomReply, _stripMentions, _stripCaptionMentions, _stripThinkBlocks, _looksLikeReasoningLeak, _emitHierarchicalSpans, _resolveRoomIdentity, _buildRoomSenderId, _buildRoomTo, _withSessionSerial, _withTurnTimeout };

// --- Plugin export ---

export default {
  id: "agentvault",
  name: "AgentVault",
  description: "End-to-end encrypted messaging between AI agent owners and their agents.",
  register(api: OpenClawPluginApi) {
    // #480 Lever 2 — say ONE thing about harness drift, or nothing at all.
    // Never throws, never blocks, silent when healthy (see harness-compat.ts).
    try {
      const compat = checkHarnessCompat();
      if (compat?.message) console.warn(compat.message);
    } catch {
      /* a compat check must never be the thing that breaks startup */
    }

    _setRuntime(api.runtime);

    // Install fetch interceptor to capture all outbound HTTP from the gateway process.
    installFetchInterceptor({
      onHttpCall: (report) => {
        // Find an active channel to emit through
        const ch = _channels.values().next().value;
        if (!ch) return;

        // Telemetry reporter is optional — may not be initialized
        if (ch.telemetry) {
          try {
            ch.telemetry.reportHttpCall({
              method: report.method,
              url: report.url,
              statusCode: report.statusCode,
              latencyMs: report.latencyMs,
              ...(report.traceId ? { traceId: report.traceId } : {}),
              ...(report.parentSpanId ? { parentSpanId: report.parentSpanId } : {}),
            });
          } catch { /* best-effort */ }
        }

        // Activity span always fires (uses WS, not telemetry reporter)
        try {
          const hostname = (() => { try { return new URL(report.url).hostname; } catch { return report.url.slice(0, 40); } })();
          ch.sendActivitySpan({
            trace_id: report.traceId ?? "",
            parent_span_id: report.parentSpanId ?? "",
            span_id: randomBytes(8).toString("hex"),
            span_type: "http",
            span_name: hostname,
            status: report.statusCode >= 400 || report.statusCode === 0 ? "error" : "ok",
            start_time: new Date(Date.now() - report.latencyMs).toISOString(),
            end_time: new Date().toISOString(),
            duration_ms: report.latencyMs,
            attributes: {
              "ai.agent.http.method": report.method,
              "ai.agent.http.url": report.url,
            },
            http_method: report.method,
            http_status_code: report.statusCode,
            http_url: report.url,
          });
        } catch { /* best-effort */ }
      },
      onLlmCall: (report) => {
        // Auto-captured from a known LLM provider's response body.
        // Only metadata (model, tokens, cost) crosses the wire; prompt and
        // completion content stay in-process inside the customer's harness.
        const ch = _channels.values().next().value;
        if (!ch) return;

        if (ch.telemetry) {
          try {
            ch.telemetry.reportLlmCall({
              model: report.model,
              latencyMs: report.latencyMs,
              tokensInput: report.tokensInput,
              tokensOutput: report.tokensOutput,
              provider: report.provider,
              ...(report.costUsd !== undefined ? { costUsd: report.costUsd } : {}),
              ...(report.traceId ? { traceId: report.traceId } : {}),
              ...(report.parentSpanId ? { parentSpanId: report.parentSpanId } : {}),
            });
          } catch { /* best-effort */ }
        }

        try {
          ch.sendActivitySpan({
            trace_id: report.traceId ?? "",
            parent_span_id: report.parentSpanId ?? "",
            span_id: randomBytes(8).toString("hex"),
            span_type: "llm",
            span_name: report.model,
            status: "ok",
            start_time: new Date(Date.now() - report.latencyMs).toISOString(),
            end_time: new Date().toISOString(),
            duration_ms: report.latencyMs,
            attributes: {
              "ai.agent.llm.model": report.model,
              "ai.agent.llm.provider": report.provider,
              "ai.agent.llm.tokens_input": report.tokensInput,
              "ai.agent.llm.tokens_output": report.tokensOutput,
              ...(report.costUsd !== undefined ? { "ai.agent.llm.cost_usd": report.costUsd } : {}),
            },
          });
        } catch { /* best-effort */ }
      },
    });

    api.registerChannel({ plugin: agentVaultPlugin });

    // --- Managed HTTP routes (OpenClaw v2026.3.2+) ---
    if (typeof api.registerHttpRoute === "function") {
      try {
        api.registerHttpRoute({
          path: "/agentvault/send",
          method: "POST",
          auth: "gateway",
          handler: async (req) => {
            const ch = _channels.values().next().value;
            if (!ch) return { status: 503, body: { ok: false, error: "Channel not started" } };
            const parsed = (typeof req.body === "string" ? JSON.parse(req.body) : req.body) as Record<string, unknown>;
            const result = await handleSendRequest(parsed, ch);
            return { status: result.status, body: result.body };
          },
        });
        api.registerHttpRoute({
          path: "/agentvault/action",
          method: "POST",
          auth: "gateway",
          handler: async (req) => {
            const ch = _channels.values().next().value;
            if (!ch) return { status: 503, body: { ok: false, error: "Channel not started" } };
            const parsed = (typeof req.body === "string" ? JSON.parse(req.body) : req.body) as Record<string, unknown>;
            const result = await handleActionRequest(parsed, ch);
            return { status: result.status, body: result.body };
          },
        });
        api.registerHttpRoute({
          path: "/agentvault/decision",
          method: "POST",
          auth: "gateway",
          handler: async (req) => {
            const ch = _channels.values().next().value;
            if (!ch) return { status: 503, body: { ok: false, error: "Channel not started" } };
            const parsed = (typeof req.body === "string" ? JSON.parse(req.body) : req.body) as Record<string, unknown>;
            const result = await handleDecisionRequest(parsed, ch);
            return { status: result.status, body: result.body };
          },
        });
        api.registerHttpRoute({
          path: "/agentvault/status",
          method: "GET",
          auth: "gateway",
          handler: async () => {
            const ch = _channels.values().next().value;
            if (!ch) return { status: 503, body: { ok: false, error: "Channel not started" } };
            const result = handleStatusRequest(ch);
            return { status: result.status, body: result.body };
          },
        });
        api.registerHttpRoute({
          path: "/agentvault/targets",
          method: "GET",
          auth: "gateway",
          handler: async () => {
            const ch = _channels.values().next().value;
            if (!ch) return { status: 503, body: { ok: false, error: "Channel not started" } };
            const result = handleTargetsRequest(ch);
            return { status: result.status, body: result.body };
          },
        });
        api.registerHttpRoute({
          path: "/agentvault/mcp-config",
          method: "GET",
          auth: "gateway",
          handler: async () => {
            const ch = _channels.values().next().value;
            if (!ch) return { status: 503, body: { ok: false, error: "Channel not started" } };
            const { handleMcpConfigRequest } = await import("./http-handlers.js");
            const agentName = ch.config?.agentName ?? "agent";
            const mcpSkillCount = ch.mcpServer?.skillCount ?? 0;
            const result = handleMcpConfigRequest(agentName, 18790, mcpSkillCount);
            return { status: result.status, body: result.body };
          },
        });
        managedRoutesRequested = true;
      } catch { /* registerHttpRoute threw (older SDK shape) — the loopback server below is the fallback */ }
    }

    // --- Event hooks (OpenClaw v2026.3.2+) ---
    if (typeof api.on === "function") {
      // Phase 4: message_sent — delivery confirmation tracking
      api.on("message_sent", async (event: MessageSentEvent) => {
        try {
          const ch = _channels.values().next().value;
          if (!ch?.telemetry) return;
          ch.telemetry.reportCustomSpan({
            traceId: randomBytes(16).toString("hex"),
            spanId: randomBytes(8).toString("hex"),
            name: "agentvault.message.delivered",
            kind: "internal",
            startTime: event.timestamp,
            endTime: event.timestamp + 1,
            attributes: {
              "agentvault.message.id": event.messageId,
              "agentvault.delivery.status": event.deliveryStatus,
              "agentvault.channel.id": event.channelId,
            },
            status: { code: event.deliveryStatus === "failed" ? 2 : 1 },
          });
        } catch { /* best-effort */ }
      });

      // Phase 6: session lifecycle hooks
      api.on("session_start", async (event: SessionStartEvent) => {
        try {
          const ch = _channels.values().next().value;
          if (!ch?.telemetry) return;
          ch.telemetry.reportCustomSpan({
            traceId: randomBytes(16).toString("hex"),
            spanId: randomBytes(8).toString("hex"),
            name: "agentvault.session.start",
            kind: "internal",
            startTime: event.timestamp,
            endTime: event.timestamp + 1,
            attributes: {
              "agentvault.session.key": event.sessionKey,
              "agentvault.agent.id": event.agentId,
            },
            status: { code: 1 },
          });
        } catch { /* best-effort */ }
      });

      api.on("session_end", async (event: SessionEndEvent) => {
        try {
          const ch = _channels.values().next().value;
          if (!ch?.telemetry) return;
          ch.telemetry.reportCustomSpan({
            traceId: randomBytes(16).toString("hex"),
            spanId: randomBytes(8).toString("hex"),
            name: "agentvault.session.end",
            kind: "internal",
            startTime: event.timestamp,
            endTime: event.timestamp + 1,
            attributes: {
              "agentvault.session.key": event.sessionKey,
              "agentvault.agent.id": event.agentId,
              ...(event.reason ? { "agentvault.session.end_reason": event.reason } : {}),
            },
            status: { code: 1 },
          });
        } catch { /* best-effort */ }
      });

      // OpenClaw v2026.3.28: before_tool_call — audit only; no policy enforcement yet (#1327)
      api.on("before_tool_call", (async (event: any) => {
        try {
          const toolName = event?.toolName;
          const agentId = event?.agentId;
          if (!toolName || !_channels.size) return {};

          // #1327: there is NO policy enforcement here. This hook used to read
          // a per-channel PolicyEnforcer property that nothing ever assigned,
          // so it always fell through to allow while reading as though policy
          // was enforced. Policies are hidden at launch; when they return, wire
          // a real enforcer (and a requireApproval path) with a test that
          // proves a DENY fires — see #1327.
          // #477: report the ALLOW too. Recording only denials would make the
          // audit trail a record of what was blocked rather than of what the
          // agent actually did, which is the gap this closes.
          _auditToolCall(toolName, agentId, "allow");
          return {};
        } catch {
          return {};
        }
      }) as (...args: any[]) => void);

      // OpenClaw v2026.3.28: before_dispatch injects AV trust context
      api.on("before_dispatch", async (event: any) => {
        try {
          const senderHub = event?.inbound?.metadata?.senderHubAddress;
          if (!senderHub || !_channels.size) return;

          // Look up sender's trust info if available
          for (const [, ch] of _channels) {
            const apiUrl = (ch as any)?.config?.apiUrl;
            if (!apiUrl) continue;

            try {
              const res = await fetch(`${apiUrl}/api/v1/hub/verify/${senderHub}`);
              if (res.ok) {
                const trust = await res.json() as Record<string, unknown>;
                // Inject trust context into the inbound metadata
                if (event.inbound.metadata) {
                  event.inbound.metadata._avTrustContext = {
                    senderHub: senderHub,
                    trustTier: trust.trust_tier,
                    verified: trust.verified,
                    certificationStatus: trust.certification_status,
                  };
                }
                console.log(`[AgentVault] Injected trust context for ${senderHub}: ${trust.trust_tier}`);
              }
            } catch {
              // Non-critical — proceed without trust context
            }
            break; // Only need one channel's API URL
          }
        } catch {
          // Non-critical — never throw from hook
        }
      });
    }

    // --- Phase 7: Agent events for trust telemetry ---
    import("./openclaw-compat.js").then(async ({ onAgentEvent, onSessionTranscriptUpdate }) => {
      onAgentEvent((event) => {
        try {
          const ch = _channels.values().next().value;
          if (!ch?.telemetry) return;
          ch.telemetry.reportCustomSpan({
            traceId: randomBytes(16).toString("hex"),
            spanId: randomBytes(8).toString("hex"),
            name: `ai.agent.event.${event.type}`,
            kind: "internal",
            startTime: event.timestamp,
            endTime: event.timestamp + 1,
            attributes: {
              "ai.agent.event.type": event.type,
              "ai.agent.id": event.agentId,
              ...(event.data ? Object.fromEntries(
                Object.entries(event.data).map(([k, v]) => [`ai.agent.event.${k}`, String(v)])
              ) : {}),
            },
            status: { code: 1 },
          });
        } catch { /* best-effort */ }
      }).catch(() => {});

      onSessionTranscriptUpdate((update) => {
        try {
          const ch = _channels.values().next().value;
          if (!ch?.telemetry) return;
          ch.telemetry.reportCustomSpan({
            traceId: randomBytes(16).toString("hex"),
            spanId: randomBytes(8).toString("hex"),
            name: "ai.agent.transcript.update",
            kind: "internal",
            startTime: update.timestamp,
            endTime: update.timestamp + 1,
            attributes: {
              "agentvault.session.key": update.sessionKey,
              "ai.agent.transcript.role": update.role ?? "unknown",
              "ai.agent.transcript.delta_chars": update.delta.length,
            },
            status: { code: 1 },
          });
        } catch { /* best-effort */ }
      }).catch(() => {});
    }).catch(() => { /* openclaw-compat import failed — older SDK */ });

    // --- Agent tool: agentvault_status ---
    // Lets the agent check its own encrypted channel status
    try {
      api.registerTool?.({
        name: "agentvault_status",
        description: "Check the AgentVault encrypted channel status, connection state, and session count.",
        parameters: {
          type: "object",
          properties: {
            accountId: {
              type: "string",
              description: "Account ID to check (default: 'default')",
            },
          },
        },
        execute: async (_id: string, params: { accountId?: string }) => {
          const id = params.accountId ?? "default";
          const ch = _channels.get(id);
          if (!ch) {
            return { content: [{ type: "text", text: JSON.stringify({ connected: false, error: "Channel not started" }) }] };
          }
          return {
            content: [{
              type: "text",
              text: JSON.stringify({
                connected: ch.state === "ready",
                state: ch.state,
                deviceId: ch.deviceId,
                sessions: ch.sessionCount,
                encrypted: true,
              }),
            }],
          };
        },
      }, { optional: true });
    } catch { /* registerTool may not be available in older OpenClaw versions */ }

    // --- Auto-reply command: /agentvault ---
    // Quick status check without AI involvement
    try {
      api.registerCommand?.({
        name: "agentvault",
        description: "Show AgentVault encrypted channel status",
        handler: () => {
          const statuses: string[] = [];
          if (_channels.size === 0) {
            statuses.push("AgentVault: no active channels");
          } else {
            for (const [id, ch] of _channels) {
              statuses.push(`AgentVault [${id}]: ${ch.state} | sessions: ${ch.sessionCount} | encrypted: yes`);
            }
          }
          return { text: statuses.join("\n") };
        },
      });
    } catch { /* registerCommand may not be available in older OpenClaw versions */ }
  },
};
